<template>
<div>
  <!--    Loader-->
  <loading-panel :loading="loading"></loading-panel>
  <!--  Breadcrumb-->
  <breadcrumb style="margin-left: -15px">
    <breadcrumb-item>
      <router-link to="/reports" exact>Report builder</router-link>
    </breadcrumb-item>
    <breadcrumb-item>
      <span>{{ report.name }}</span>
    </breadcrumb-item>
  </breadcrumb>

  <div class="row no-scrollbar" style="overflow-x: scroll">
    <div class="col">
      <card>
        <div v-if="downloading" role="status" aria-live="polite" aria-atomic="true" class="download-progress mb-4">
          <p class="download-progress-label text-white mb-1">{{ downloadPhaseLabel }}</p>
          <el-progress :percentage="Number(percent_downloaded)" color="#e14eca" />
          <p class="download-progress-detail mb-1">
            <span>{{ downloadRowsLabel }}</span>
            <span v-if="downloadEtaLabel"> &middot; {{ downloadEtaLabel }}</span>
          </p>
          <p v-if="retryingAttempt > 0" class="download-progress-retry mb-2">
            Retrying... attempt {{ retryingAttempt }} of 3
          </p>
          <base-button class="mt-1" size="sm" type="default" :disabled="cancelling" v-on:click="cancelDownload()">
            {{ cancelling ? 'Cancelling...' : 'Cancel' }}
          </base-button>
        </div>
        <h4 class="card-title">{{ report.name }}</h4>
        <info-hint v-if="groupScope && groupScope.limited === 1" variant="warning" icon="icon-alert-circle-exc" class="mb-3">
          This report shows a subset of the company's data. Your account has access to {{ groupScope.accessible_groups }} of {{ groupScope.total_groups }} groups, and rows from the other groups are not included.
        </info-hint>

        <filter-bar
          :key="filterBarKey"
          :filters="filterSchema"
          :initial-value="filterValues"
          :result-count="totalRows"
          :loading="loading"
          :per-page="perPage"
          :per-page-options="perPageOptions"
          :quick-actions="presetQuickActions"
          @change="onFiltersChanged"
          @update:perPage="onPerPageChange"
          @quick-action="onQuickAction"
        >
          <template #actions>
            <base-button icon class="btn-link text-white" v-if="hasPermissions('download_data') && !downloading" v-on:click="downloadCSV()">
              <i class="tim-icons icon-cloud-download-93"></i>
            </base-button>
          </template>
        </filter-bar>

        <div class="summary-mode-row d-flex align-items-center flex-wrap">
          <span class="summary-mode-label">View</span>
          <segmented-toggle
            v-model="summaryModeValue"
            :options="[{value: 'detail', label: 'Detail'}, {value: 'summary', label: 'Summary'}]"
            size="sm"
          />
          <template v-if="summaryActive">
            <span class="summary-mode-label">grouped by</span>
            <base-input class="mb-0" style="width: 190px">
              <el-select class="select-primary" v-model="summaryGroupBy" placeholder="Group by" size="small" @change="onSummaryConfigChange">
                <el-option class="select-primary" v-for="f in summaryGroupOptions" :key="'SG' + f.field" :label="fieldLabel(f.field)" :value="f.field" />
              </el-select>
            </base-input>
            <template v-if="summaryGroupByIsDate">
              <span class="summary-mode-label">per</span>
              <base-input class="mb-0" style="width: 140px">
                <el-select class="select-primary" v-model="summaryBucket" placeholder="Bucket" size="small" @change="onSummaryConfigChange">
                  <el-option class="select-primary" v-for="b in summaryBucketOptions" :key="b.value" :label="b.label" :value="b.value" />
                </el-select>
              </base-input>
            </template>
          </template>
        </div>
        <div class="row" style="overflow-x: scroll">
          <div class="col">
            <b-table v-if="data.length > 0" striped hover :items="data"
                     :fields="tableFields"
                     no-local-sorting
                     :sort-by.sync="sortBy"
                     :sort-desc.sync="sortDesc"
                     @sort-changed="onSortChanged">
              <!--          Customer fields-->
              <template v-slot:cell(birthday)="row">
                <span>{{getFormattedDate(row.item.birthday)}}</span>
              </template>
              <template v-slot:cell(date_joined)="row">
                <span v-if="row.item.date_joined && !row.item.date_joined.includes('0000')">
                  {{getFormattedDate(row.item.date_joined)}}, {{getTime(row.item.date_joined)}}
                </span>
                <span v-else>-</span>
              </template>
              <template v-slot:cell(date_last_visited)="row">
                <span v-if="row.item.date_last_visited && !row.item.date_last_visited.includes('0000')">
                  {{getFormattedDate(row.item.date_last_visited)}}, {{getTime(row.item.date_last_visited)}}
                </span>
                <span v-else>-</span>
              </template>
              <template v-slot:cell(phone)="row">
                <span v-if="row.item.phone">{{row.item.phone}}</span>
                <span v-else>-</span>
              </template>
              <template v-slot:cell(first_name)="row">
                <span v-if="row.item.first_name">{{row.item.first_name}}</span>
                <span v-else>-</span>
              </template>
              <template v-slot:cell(customer_credit)="row">
                <span v-if="row.item.customer_credit >=0">{{getAmount(row.item.customer_credit)}}</span>
              </template>
              <template v-slot:cell(last_name)="row">
                <span v-if="row.item.last_name">{{row.item.last_name}}</span>
                <span v-else>-</span>
              </template>
              <template v-slot:cell(address_line_1)="row">
                <span v-if="row.item.address_line_1">{{row.item.address_line_1}}</span>
                <span v-else>-</span>
              </template>
              <template v-slot:cell(address_line_2)="row">
                <span v-if="row.item.address_line_2">{{row.item.address_line_2}}</span>
                <span v-else>-</span>
              </template>
              <template v-slot:cell(city)="row">
                <span v-if="row.item.city">{{row.item.city}}</span>
                <span v-else>-</span>
              </template>
              <template v-slot:cell(state)="row">
                <span v-if="row.item.state">{{row.item.state}}</span>
                <span v-else>-</span>
              </template>
              <template v-slot:cell(zip)="row">
                <span v-if="row.item.zip">{{row.item.zip}}</span>
                <span v-else>-</span>
              </template>
              <template v-slot:cell(country)="row">
                <span v-if="row.item.country">{{row.item.country}}</span>
                <span v-else>-</span>
              </template>
              <template v-slot:cell(opted_out_of_posting_photos)="row">
                <span v-if="row.item.opted_out_of_posting_photos === 1">Yes</span>
                <span v-if="row.item.opted_out_of_posting_photos === 0">No</span>
                <span v-if="row.item.opted_out_of_posting_photos === null">-</span>
              </template>
              <template v-slot:cell(opted_out_of_receiving_emails)="row">
                <span v-if="row.item.opted_out_of_receiving_emails === 1">Yes</span>
                <span v-if="row.item.opted_out_of_receiving_emails === 0">No</span>
                <span v-if="row.item.opted_out_of_receiving_emails === null">-</span>
              </template>
              <template v-slot:cell(referral_source)="row">
                <span v-if="row.item.referral_source">{{ capitalizeFirstLetter(row.item.referral_source) }}</span>
                <span v-else>-</span>
              </template>
              <template v-slot:cell(player_experience_level)="row">
                <span v-if="row.item.player_experience_level">{{ capitalizeFirstLetter(row.item.player_experience_level) }}</span>
                <span v-else>-</span>
              </template>
              <template v-slot:cell(tags)="row">
                <span v-if="!row.item.tags">-</span>
                <span v-else>{{ row.item.tags }}</span>
              </template>
              <!--          Game fields-->
              <template v-slot:cell(start_time)="row">
                <span v-if="report.report_type_id === 10 && row.item.start_time">{{getFormattedDate(row.item.start_time)}}, {{getTime(row.item.start_time)}}</span>
                <span v-else-if="report.report_type_id === 10">-</span>
                <span v-else>{{getTime(row.item.start_time)}}</span>
              </template>
              <template v-slot:cell(end_time)="row">
                <span v-if="report.report_type_id === 10 && row.item.end_time">{{getFormattedDate(row.item.end_time)}}, {{getTime(row.item.end_time)}}</span>
                <span v-else-if="report.report_type_id === 10">-</span>
                <span v-else>{{getTime(row.item.end_time)}}</span>
              </template>
              <template v-slot:cell(booking_time)="row">
                <span v-if="row.item.booking_time">{{getTime(row.item.booking_time)}}</span>
                <span v-else>-</span>
              </template>
              <template v-slot:cell(pre_game_notes)="row">
                <span v-if="row.item.pre_game_notes">{{row.item.pre_game_notes}}</span>
                <span v-else>-</span>
              </template>
              <template v-slot:cell(team_experience_level)="row">
                <span v-if="row.item.team_experience_level">{{row.item.team_experience_level}}</span>
                <span v-else>-</span>
              </template>
              <template v-slot:cell(post_game_notes)="row">
                <span v-if="row.item.post_game_notes">{{row.item.post_game_notes}}</span>
                <span v-else>-</span>
              </template>
              <template v-slot:cell(completed)="row">
                <span v-if="row.item.completed">Yes</span>
                <span v-else>No</span>
              </template>
              <template v-slot:cell(booking_date)="row">
                <span v-if="row.item.booking_date">{{getFormattedDate(row.item.booking_date)}}</span>
                <span v-else>-</span>
              </template>
              <!--          Transactions-->
              <template v-slot:cell(customer_first_name)="row">
                <span v-if="row.item.customer_first_name">{{row.item.customer_first_name}}</span>
                <span v-else>-</span>
              </template>
              <template v-slot:cell(customer_last_name)="row">
                <span v-if="row.item.customer_last_name">{{row.item.customer_last_name}}</span>
                <span v-else>-</span>
              </template>
              <template v-slot:cell(customer_email)="row">
                <span v-if="row.item.customer_email">{{row.item.customer_email}}</span>
                <span v-else>-</span>
              </template>
              <template v-slot:cell(customer_phone)="row">
                <span v-if="row.item.customer_phone">{{row.item.customer_phone}}</span>
                <span v-else>-</span>
              </template>
              <template v-slot:cell(transaction_id)="row">
                <span v-if="row.item.transaction_id">#{{row.item.transaction_id}}</span>
                <span v-else>-</span>
              </template>
              <template v-slot:cell(transaction_has_due_amount)="row">
                <span v-if="row.item.transaction_has_due_amount">Yes</span>
                <span v-else>No</span>
              </template>
              <template v-slot:cell(status)="row">
                <span v-if="report.report_type_id === 9">{{ row.item.status || '-' }}</span>
                <span v-else-if="row.item.status">Active</span>
                <span v-else>Cancelled</span>
              </template>
              <template v-slot:cell(tax)="row">
                <span>{{getAmount(row.item.tax)}}</span>
              </template>
              <template v-slot:cell(discount)="row">
                <span>{{getAmount(row.item.discount)}}</span>
              </template>
              <template v-slot:cell(fee)="row">
                <span>{{getAmount(row.item.fee)}}</span>
              </template>
              <template v-slot:cell(tips)="row">
                <span>{{getAmount(row.item.tips)}}</span>
              </template>
              <template v-slot:cell(tip_date)="row">
                <span v-if="row.item.tip_date">{{formatTipDates(row.item.tip_date)}}</span>
                <span v-else>-</span>
              </template>
              <template v-slot:cell(tip_assignees)="row">
                <span v-if="row.item.tip_assignees">{{row.item.tip_assignees}}</span>
                <span v-else>-</span>
              </template>
              <template v-slot:cell(price)="row">
                <span>{{getAmount(row.item.price)}}</span>
              </template>
              <template v-slot:cell(paid)="row">
                <span>{{getAmount(row.item.paid)}}</span>
              </template>
              <template v-slot:cell(total)="row">
                <span>{{getAmount(row.item.total)}}</span>
              </template>
              <template  v-slot:cell(refunded)="row">
                <span>{{getAmount(row.item.refunded)}}</span>
              </template>
              <template v-slot:cell(due)="row">
                <span>{{getAmount(row.item.due)}}</span>
              </template>
              <template v-slot:cell(promo_codes)="row">
                <span v-if="!row.item.promo_codes">-</span>
                <span v-else>{{ row.item.promo_codes }}</span>
              </template>
              <template v-slot:cell(tax_names)="row">
                <span v-if="!row.item.tax_names">-</span>
                <span v-else>{{ row.item.tax_names }}</span>
              </template>
              <template v-slot:cell(purchases_in_transaction)="row">
                <span v-if="!row.item.purchases_in_transaction">-</span>
                <span v-else>{{ row.item.purchases_in_transaction }}</span>
              </template>
              <template v-slot:cell(transaction_date)="row">
                <span v-if="row.item.transaction_date">{{getFormattedDate(row.item.transaction_date)}}, {{getTime(row.item.transaction_date)}}</span>
                <span v-else>-</span>
              </template>
              <template v-slot:cell(source)="row">
                <span v-if="!row.item.source">-</span>
                <span v-else>{{ capitalizeFirstLetterRemoveUnderscore(row.item.source) }}</span>
              </template>
              <template v-slot:cell(medium)="row">
                <span v-if="!row.item.medium">-</span>
                <span v-else>{{ formatUtmValue(row.item.medium) }}</span>
              </template>
              <template v-slot:cell(campaign)="row">
                <span v-if="!row.item.campaign">-</span>
                <span v-else>{{ formatUtmValue(row.item.campaign) }}</span>
              </template>
<!--              Purchases-->
              <template v-slot:cell(purchase_name)="row">
                <span v-if="row.item.purchase_name && row.item.purchase_type === 'Gift card'">{{getGiftCardCode(row.item.purchase_name)}}</span>
                <span v-if="row.item.purchase_name && row.item.purchase_type !== 'Gift card'">{{row.item.purchase_name}}</span>
                <span v-if="!row.item.purchase_name">-</span>
              </template>
              <template v-slot:cell(purchase_time)="row">
                <span v-if="row.item.purchase_time">{{getFormattedDate(row.item.purchase_time)}}, {{getTime(row.item.purchase_time)}}</span>
                <span v-else>-</span>
              </template>
<!--              Payments-->
              <template v-slot:cell(payment_gateway_transaction_id)="row">
                <span v-if="row.item.payment_gateway_transaction_id">{{row.item.payment_gateway_transaction_id}}</span>
                <span v-else>-</span>
              </template>
              <template v-slot:cell(payment_method)="row">
                <span>{{getPaymentMethod(row.item.payment_method)}}</span>
              </template>
              <template v-slot:cell(expiry)="row">
                <span>{{getExpiry(row.item.expiry)}}</span>
              </template>
              <template v-slot:cell(payment_collected_by)="row">
                <span v-if="row.item.payment_collected_by">{{row.item.payment_collected_by}}</span>
                <span v-else>-</span>
              </template>
              <template v-slot:cell(last_four)="row">
                <span v-if="row.item.last_four">{{row.item.last_four}}</span>
                <span v-else>-</span>
              </template>
              <template v-slot:cell(card_type)="row">
                <span v-if="row.item.card_type">{{capitalizeFirstLetter(row.item.card_type)}}</span>
                <span v-else>-</span>
              </template>
<!--              Promo codes-->
              <template v-slot:cell(description)="row">
                <span v-if="row.item.description">{{row.item.description}}</span>
                <span v-else>-</span>
              </template>
              <template v-slot:cell(games)="row">
                <span v-if="row.item.games">{{row.item.games}}</span>
                <span v-else>-</span>
              </template>
              <template v-slot:cell(redemption_type)="row">
                <span v-if="row.item.redemption_type === 0">Percentage discount</span>
                <span v-if="row.item.redemption_type === 1">Value discount</span>
              </template>
              <template v-slot:cell(value_type)="row">
                <span v-if="row.item.value_type === 0">Per transaction</span>
                <span v-if="row.item.value_type === 2">Per participant</span>
              </template>
              <template v-slot:cell(value_amount)="row">
                <span>{{getAmount(row.item.value_amount)}}</span>
              </template>
              <template v-slot:cell(quantity)="row">
                <span v-if="row.item.quantity === -1">Unlimited</span>
                <span v-if="row.item.quantity > -1">{{ row.item.quantity }}</span>
              </template>
              <template v-slot:cell(purchase_start_date)="row">
                <span v-if="row.item.purchase_start_date">{{getFormattedDate(row.item.purchase_start_date)}}</span>
                <span v-else>-</span>
              </template>
              <template v-slot:cell(purchase_end_date)="row">
                <span v-if="row.item.purchase_end_date">{{getFormattedDate(row.item.purchase_end_date)}}</span>
                <span v-else>-</span>
              </template>
              <template v-slot:cell(event_start_date)="row">
                <span v-if="row.item.event_start_date">{{getFormattedDate(row.item.event_start_date)}}</span>
                <span v-else>-</span>
              </template>
              <template v-slot:cell(event_end_date)="row">
                <span v-if="row.item.event_end_date">{{getFormattedDate(row.item.event_end_date)}}</span>
                <span v-else>-</span>
              </template>
              <template v-slot:cell(event_start_time)="row">
                <span v-if="row.item.event_start_time">{{getLocalTime(row.item.event_start_time)}}</span>
                <span v-else>-</span>
              </template>
              <template v-slot:cell(event_end_time)="row">
                <span v-if="row.item.event_end_time">{{getLocalTime(row.item.event_end_time)}}</span>
                <span v-else>-</span>
              </template>
              <template v-slot:cell(can_be_combined)="row">
                <span v-if="row.item.can_be_combined === 1">Yes</span>
                <span v-if="row.item.can_be_combined === 0">No</span>
              </template>
              <template v-slot:cell(applicable_to_miscellaneous_items)="row">
                <span v-if="row.item.applicable_to_miscellaneous_items === 1">Yes</span>
                <span v-if="row.item.applicable_to_miscellaneous_items === 0">No</span>
              </template>
              <template v-slot:cell(issued_by_campaign)="row">
                <span v-if="row.item.issued_by_campaign === 1">Yes</span>
                <span v-if="row.item.issued_by_campaign === 0">No</span>
              </template>
              <template v-slot:cell(internal_only)="row">
                <span v-if="row.item.internal_only === 1">Yes</span>
                <span v-if="row.item.internal_only === 0">No</span>
              </template>

<!--              Custom fields-->
              <template v-slot:cell(response_time)="row">
                <span v-if="row.item.response_time && !row.item.response_time.includes('0000')">
                  {{getFormattedDate(row.item.response_time)}}, {{getTime(row.item.response_time)}}
                </span>
                <span v-else>-</span>
              </template>

<!--              Project management — tickets-->
              <template v-slot:cell(ticket_key)="row">
                <span v-if="row.item.ticket_key">{{row.item.ticket_key}}</span>
                <span v-else>-</span>
              </template>
              <template v-slot:cell(title)="row">
                <span v-if="row.item.title">{{row.item.title}}</span>
                <span v-else>-</span>
              </template>
              <template v-slot:cell(project_name)="row">
                <span v-if="row.item.project_name">{{row.item.project_name}}</span>
                <span v-else>-</span>
              </template>
              <template v-slot:cell(ticket_type)="row">
                <span v-if="row.item.ticket_type">{{row.item.ticket_type}}</span>
                <span v-else>-</span>
              </template>
              <template v-slot:cell(priority)="row">
                <span v-if="row.item.priority">{{priorityLabel(row.item.priority)}}</span>
                <span v-else>-</span>
              </template>
              <template v-slot:cell(assignee)="row">
                <span v-if="row.item.assignee && row.item.assignee.trim()">{{row.item.assignee}}</span>
                <span v-else>-</span>
              </template>
              <template v-slot:cell(reporter)="row">
                <span v-if="row.item.reporter && row.item.reporter.trim()">{{row.item.reporter}}</span>
                <span v-else>-</span>
              </template>
              <template v-slot:cell(created_date)="row">
                <span v-if="row.item.created_date && !String(row.item.created_date).includes('0000')">
                  {{getFormattedDate(row.item.created_date)}}, {{getTime(row.item.created_date)}}
                </span>
                <span v-else>-</span>
              </template>
              <template v-slot:cell(due_date)="row">
                <span v-if="row.item.due_date && !String(row.item.due_date).includes('0000')">{{getFormattedDate(row.item.due_date)}}</span>
                <span v-else>-</span>
              </template>
              <template v-slot:cell(resolved_date)="row">
                <span v-if="row.item.resolved_date && !String(row.item.resolved_date).includes('0000')">
                  {{getFormattedDate(row.item.resolved_date)}}, {{getTime(row.item.resolved_date)}}
                </span>
                <span v-else>-</span>
              </template>
              <template v-slot:cell(last_updated)="row">
                <span v-if="row.item.last_updated && !String(row.item.last_updated).includes('0000')">
                  {{getFormattedDate(row.item.last_updated)}}, {{getTime(row.item.last_updated)}}
                </span>
                <span v-else>-</span>
              </template>
              <template v-slot:cell(time_estimate)="row">
                <span>{{formatMinutes(row.item.time_estimate)}}</span>
              </template>
              <template v-slot:cell(time_logged)="row">
                <span>{{formatMinutes(row.item.time_logged)}}</span>
              </template>

<!--              Project management — time tracking-->
              <template v-slot:cell(user)="row">
                <span v-if="row.item.user && row.item.user.trim()">{{row.item.user}}</span>
                <span v-else>-</span>
              </template>
              <template v-slot:cell(ticket)="row">
                <span v-if="row.item.ticket">{{row.item.ticket}}</span>
                <span v-else>-</span>
              </template>
              <template v-slot:cell(ticket_title)="row">
                <span v-if="row.item.ticket_title">{{row.item.ticket_title}}</span>
                <span v-else>-</span>
              </template>
              <template v-slot:cell(category)="row">
                <span v-if="row.item.category">{{row.item.category}}</span>
                <span v-else>-</span>
              </template>
              <template v-slot:cell(duration)="row">
                <span>{{formatMinutes(row.item.duration)}}</span>
              </template>
              <template v-slot:cell(notes)="row">
                <span v-if="row.item.notes">{{row.item.notes}}</span>
                <span v-else>-</span>
              </template>
              <template v-slot:cell(is_running)="row">
                <span v-if="row.item.is_running">Yes</span>
                <span v-else>No</span>
              </template>
              <template v-slot:cell(created_at)="row">
                <span v-if="row.item.created_at && !String(row.item.created_at).includes('0000')">
                  {{getFormattedDate(row.item.created_at)}}, {{getTime(row.item.created_at)}}
                </span>
                <span v-else>-</span>
              </template>

<!--              Resource efficiency (type 11)-->
              <template v-slot:cell(resource_name)="row">
                <span v-if="row.item.resource_name">{{row.item.resource_name}}</span>
                <span v-else>-</span>
              </template>
              <template v-slot:cell(resource_type)="row">
                <span v-if="row.item.resource_type">{{row.item.resource_type}}</span>
                <span v-else>-</span>
              </template>
              <template v-slot:cell(resource_category)="row">
                <span v-if="row.item.resource_category">{{row.item.resource_category}}</span>
                <span v-else>-</span>
              </template>
              <template v-slot:cell(capacity)="row">
                <span v-if="row.item.capacity != null">{{row.item.capacity}}</span>
                <span v-else>-</span>
              </template>
              <template v-slot:cell(availability_start_date)="row">
                <span v-if="row.item.availability_start_date">{{getFormattedDate(row.item.availability_start_date)}}</span>
                <span v-else>-</span>
              </template>
              <template v-slot:cell(availability_end_date)="row">
                <span v-if="row.item.availability_end_date">{{getFormattedDate(row.item.availability_end_date)}}</span>
                <span v-else>-</span>
              </template>
              <template v-slot:cell(availability_start_time)="row">
                <span v-if="row.item.availability_start_time">{{getTime(row.item.availability_start_time)}}</span>
                <span v-else>-</span>
              </template>
              <template v-slot:cell(availability_end_time)="row">
                <span v-if="row.item.availability_end_time">{{getTime(row.item.availability_end_time)}}</span>
                <span v-else>-</span>
              </template>
              <template v-slot:cell(available_hours)="row">
                <span>{{formatHours(row.item.available_hours)}}</span>
              </template>
              <template v-slot:cell(booked_hours)="row">
                <span>{{formatHours(row.item.booked_hours)}}</span>
              </template>
              <template v-slot:cell(idle_hours)="row">
                <span>{{formatHours(row.item.idle_hours)}}</span>
              </template>
              <template v-slot:cell(utilization)="row">
                <span>{{formatUtilization(row.item.utilization)}}</span>
              </template>
              <template v-slot:cell(bookings_assigned)="row">
                <span v-if="row.item.bookings_assigned != null">{{row.item.bookings_assigned}}</span>
                <span v-else>-</span>
              </template>

<!--              Staff activity (type 12)-->
              <template v-slot:cell(staff_name)="row">
                <span v-if="row.item.staff_name && row.item.staff_name.trim()">{{row.item.staff_name}}</span>
                <span v-else>-</span>
              </template>
              <template v-slot:cell(staff_email)="row">
                <span v-if="row.item.staff_email">{{row.item.staff_email}}</span>
                <span v-else>-</span>
              </template>
              <template v-slot:cell(staff_nickname)="row">
                <span v-if="row.item.staff_nickname">{{row.item.staff_nickname}}</span>
                <span v-else>-</span>
              </template>
              <template v-slot:cell(staff_role)="row">
                <span v-if="row.item.staff_role">{{row.item.staff_role}}</span>
                <span v-else>-</span>
              </template>
              <template v-slot:cell(event_name)="row">
                <span v-if="row.item.event_name">{{row.item.event_name}}</span>
                <span v-else>-</span>
              </template>
              <template v-slot:cell(event_date)="row">
                <span v-if="row.item.event_date && !String(row.item.event_date).includes('0000')">{{getFormattedDate(row.item.event_date)}}</span>
                <span v-else>-</span>
              </template>
              <template v-slot:cell(event_time)="row">
                <span v-if="row.item.event_time">{{getTime(row.item.event_time)}}</span>
                <span v-else>-</span>
              </template>
              <template v-slot:cell(order_number)="row">
                <span v-if="row.item.order_number">#{{row.item.order_number}}</span>
                <span v-else>-</span>
              </template>
              <template v-slot:cell(involvement)="row">
                <span v-if="row.item.involvement">{{row.item.involvement}}</span>
                <span v-else>-</span>
              </template>
              <template v-slot:cell(tips_collected)="row">
                <span>{{getAmount(row.item.tips_collected)}}</span>
              </template>
              <!--          Waiver fields (type 13)-->
              <template v-slot:cell(signed_date)="row">
                <span v-if="row.item.signed_date">{{getFormattedDate(row.item.signed_date)}}, {{getTime(row.item.signed_date)}}</span>
                <span v-else>-</span>
              </template>
              <!--          Checklist fields (types 14 and 15)-->
              <template v-slot:cell(started_at)="row">
                <span v-if="row.item.started_at">{{getFormattedDate(row.item.started_at)}}, {{getTime(row.item.started_at)}}</span>
                <span v-else>-</span>
              </template>
              <template v-slot:cell(completed_at)="row">
                <span v-if="row.item.completed_at">{{getFormattedDate(row.item.completed_at)}}, {{getTime(row.item.completed_at)}}</span>
                <span v-else>-</span>
              </template>
              <template v-slot:cell(reviewed_at)="row">
                <span v-if="row.item.reviewed_at">{{getFormattedDate(row.item.reviewed_at)}}, {{getTime(row.item.reviewed_at)}}</span>
                <span v-else>-</span>
              </template>
              <template v-slot:cell(done_at)="row">
                <span v-if="row.item.done_at">{{getFormattedDate(row.item.done_at)}}, {{getTime(row.item.done_at)}}</span>
                <span v-else>-</span>
              </template>
              <template v-slot:cell(percent_complete)="row">
                <span v-if="row.item.percent_complete !== null && row.item.percent_complete !== undefined">{{row.item.percent_complete}}%</span>
                <span v-else>-</span>
              </template>

<!--              Totals row (resource efficiency report only)-->
              <template #custom-foot="data">
                <b-tr v-if="report.report_type_id === 11 && resourceEfficiencyTotals && !summaryActive" class="resource-efficiency-totals-row">
                  <b-td v-for="(f, idx) in data.fields" :key="'foot-' + f.key">
                    <strong v-if="idx === 0">Totals</strong>
                    <strong v-else-if="f.key === 'available_hours'">{{formatHours(resourceEfficiencyTotals.available_hours)}}</strong>
                    <strong v-else-if="f.key === 'booked_hours'">{{formatHours(resourceEfficiencyTotals.booked_hours)}}</strong>
                    <strong v-else-if="f.key === 'idle_hours'">{{formatHours(resourceEfficiencyTotals.idle_hours)}}</strong>
                    <strong v-else-if="f.key === 'utilization'">{{formatUtilization(resourceEfficiencyTotals.utilization)}}</strong>
                    <span v-else></span>
                  </b-td>
                </b-tr>
              </template>
            </b-table>

          </div>
        </div>

        <b-pagination  @click.native="generateReport()"
                       v-model="currentPage"
                       :total-rows="totalRows"
                       :per-page="perPage"
        ></b-pagination>

      </card>
    </div>
  </div>
</div>
</template>

<script>
import {Auth} from "aws-amplify";
import BreadcrumbItem from "@/components/Breadcrumb/BreadcrumbItem";
import Breadcrumb from "@/components/Breadcrumb/Breadcrumb";
import {API_LOCATION} from "@/api-config";
import swal from "sweetalert2";
import { BTable, BPagination, BTr, BTd } from 'bootstrap-vue'
import {formattedDate, time} from "@/plugins/dateFormatter";
import currencies from "@/assets/currencies.json";
import axios from "axios";
import {gameToEventLabel} from "@/util/eventLabels";
import { formatUtmValue } from "@/util/utmAttribution";
import { neutralizeCsvFormula } from "@/util/listPage";
import _ from "lodash";
import {Progress, Select, Option} from "element-ui";

export default {
  name: "GeneratedReport",
  components: {
    BreadcrumbItem,
    Breadcrumb,
    BTable,
    BPagination,
    BTr,
    BTd,
    [Progress.name]: Progress,
    [Select.name]: Select,
    [Option.name]: Option,
  },
  data() {
    return {
      custom_fields: [],
      groupScope: null,
      downloading: false,
      downloadCancelled: false,
      percent_downloaded: 0,
      rowsFetched: 0,
      downloadStartedAt: null,
      retryingAttempt: 0,
      downloadPhase: null,
      cancelling: false,
      loading: false,
      currencies: currencies.currencies,
      number_format: JSON.parse(localStorage.getItem('group')).number_format,
      currency_symbol: '$',
      currency: JSON.parse(localStorage.getItem('group')).currency,
      currency_format: JSON.parse(localStorage.getItem('group')).currency_format,
      date_format: JSON.parse(localStorage.getItem('group')).date_format,
      time_format: JSON.parse(localStorage.getItem('group')).time_format,
      groups: [],
      data: [],
      fields: [],
      fieldMeta: [],
      summaryActive: false,
      summaryGroupBy: null,
      summaryBucket: 'month',
      summarySeeded: false,
      sortBy: '',
      sortDesc: false,
      resourceEfficiencyTotals: null,
      resource_categories: [],
      games: [],
      promo_codes: [],
      purchase_types: [],
      purchases: [],
      company_users: [],
      checklists: [],
      projects: [],
      ticket_statuses: [],
      time_log_categories: [],
      team_members: [],
      report: '',
      currentPage: 1,
      totalRows: 0,
      perPage: 10,
      perPageOptions: [5, 10, 25, 50],
      // FilterBar value model (mirrors `filters` below, which stays as the internal model
      // that buildUrl()/getAllData() read). FilterBar is re-seeded from this on remount.
      filterValues: this.buildInitialFilterValues(),
      filterBarKey: 0,
      presetFilters: {},
      canEditReport: false,
      savingPreset: false,
      pendingPresetPurchases: {},
      firstLoadDone: false,
      filters: {
        //Project management — tickets
        projects: {
          value: '',
          actual: ''
        },
        statuses: {
          value: '',
          actual: ''
        },
        priorities: {
          value: '',
          actual: ''
        },
        assignees: {
          value: '',
          actual: ''
        },
        reporters: {
          value: '',
          actual: ''
        },
        title: {
          value: '',
          actual: ''
        },
        ticket_key: {
          value: '',
          actual: ''
        },
        due_date: {
          value_to: '',
          value_from: '',
          actual_to: '',
          actual_from: ''
        },
        resolved_date: {
          value_to: '',
          value_from: '',
          actual_to: '',
          actual_from: ''
        },
        //Project management — time tracking
        users: {
          value: '',
          actual: ''
        },
        categories: {
          value: '',
          actual: ''
        },
        is_running: {
          value: '',
          actual: ''
        },
        // OTC-2926 time tracking location filters (report type 10)
        location_checks: {
          value: '',
          actual: ''
        },
        meters_from_venue: {
          value_to: '',
          value_from: '',
          actual_to: '',
          actual_from: ''
        },
        tips: {
          value_from: '',
          value_to: '',
          actual_to: '',
          actual_from: '',
        },
        promo_code_name: {
          value: '',
          actual: ''
        },
        description: {
          value: '',
          actual: ''
        },
        redemption_type: {
          value: '',
          actual: ''
        },
        value_type: {
          value: '',
          actual: ''
        },
        value_amount: {
          value_from: '',
          value_to: '',
          actual_to: '',
          actual_from: '',
        },
        can_be_combined: {
          value: '',
          actual: ''
        },
        issued_by_campaign: {
          value: '',
          actual: ''
        },
        number_of_times_used: {
          value_from: '',
          value_to: '',
          actual_to: '',
          actual_from: '',
        },
        games: {
          value: '',
          actual: ''
        },
        applicable_to_miscellaneous_items: {
          value: '',
          actual: ''
        },
        internal_only: {
          value: '',
          actual: ''
        },
        transaction_has_due_amount: {
          value: '',
          actual: ''
        },
        promo_codes: {
          value: '',
          actual: '',
          with_promo_code_value: '',
          with_promo_code_actual: ''
        },
        tip_assignees: {
          value: '',
          actual: ''
        },
        transaction_notes: {
          value: '',
          actual: ''
        },
        tax_names: {
          value: '',
          actual: ''
        },
        date_joined: {
          value_to: '',
          value_from: '',
          actual_to: '',
          actual_from: ''
        },
        date_last_visited: {
          value_to: '',
          value_from: '',
          actual_to: '',
          actual_from: ''
        },
        payment_collected_by: {
          value: '',
          actual: ''
        },
        payment_methods: {
          value: '',
          actual: ''
        },
        expiry: {
          value: '',
          actual: ''
        },
        last_four: {
          value: '',
          actual: ''
        },
        card_type: {
          value: '',
          actual: ''
        },
        payment_gateway_id: {
          value: '',
          actual: ''
        },
        group_name: {
          value: '',
          actual: ''
        },
        first_name: {
          value: '',
          actual: ''
        },
        last_name: {
          value: '',
          actual: ''
        },
        source: {
          value: '',
          actual: ''
        },
        medium: { value: '', actual: '' },
        campaign: { value: '', actual: '' },
        referrer: { value: '', actual: '' },
        traffic_attribution: { value: '', actual: '' },
        customer_credit: {
          value: '',
          actual: ''
        },
        opted_out_of_receiving_emails: {
          value: '',
          actual: ''
        },
        birthday: {
          value_to: '',
          value_from: '',
          actual_to: '',
          actual_from: ''
        },
        state: {
          value: '',
          actual: ''
        },
        email: {
          value: '',
          actual: ''
        },
        phone: {
          value: '',
          actual: ''
        },
        address: {
          value: '',
          actual: ''
        },
        city: {
          value: '',
          actual: ''
        },
        zip: {
          value: '',
          actual: ''
        },
        country: {
          value: '',
          actual: ''
        },
        opted_out_of_posting_photos: {
          value: '',
          actual: ''
        },
        player_experience_level: {
          value: '',
          actual: ''
        },
        referral_source: {
          value: '',
          actual: ''
        },
        tag: {
          value: '',
          actual: ''
        },
        start_time: {
          value_to: '',
          value_from: '',
          actual_to: '',
          actual_from: ''
        },
        end_time: {
          value_to: '',
          value_from: '',
          actual_to: '',
          actual_from: ''
        },
        game_name: {
          value: '',
          actual: ''
        },
        team_experience_level: {
          value: '',
          actual: ''
        },
        pre_game_notes: {
          value: '',
          actual: ''
        },
        post_game_notes: {
          value: '',
          actual: ''
        },
        completed: {
          value: '',
          actual: ''
        },
        game_master: {
          value: '',
          actual: ''
        },
        booking_date: {
          value_to: '',
          value_from: '',
          actual_to: '',
          actual_from: ''
        },
        booking_time: {
          value_to: '',
          value_from: '',
          actual_to: '',
          actual_from: ''
        },
        number_of_hints: {
          value_to: '',
          value_from: '',
          actual_to: '',
          actual_from: ''
        },
        number_of_breaks: {
          value_to: '',
          value_from: '',
          actual_to: '',
          actual_from: ''
        },
        transaction_id: {
          value: '',
          actual: ''
        },
        customer_first_name: {
          value: '',
          actual: ''
        },
        customer_last_name: {
          value: '',
          actual: ''
        },
        customer_email: {
          value: '',
          actual: ''
        },
        customer_phone: {
          value: '',
          actual: ''
        },
        customer_address: {
          value: '',
          actual: ''
        },
        customer_city: {
          value: '',
          actual: ''
        },
        customer_state: {
          value: '',
          actual: ''
        },
        customer_zip: {
          value: '',
          actual: ''
        },
        price: {
          value_from: '',
          value_to: '',
          actual_to: '',
          actual_from: ''
        },
        fee: {
          value_from: '',
          value_to: '',
          actual_to: '',
          actual_from: ''
        },
        discount: {
          value_from: '',
          value_to: '',
          actual_to: '',
          actual_from: ''
        },
        tax: {
          value_from: '',
          value_to: '',
          actual_to: '',
          actual_from: ''
        },
        total: {
          value_from: '',
          value_to: '',
          actual_to: '',
          actual_from: ''
        },
        paid: {
          value_from: '',
          value_to: '',
          actual_to: '',
          actual_from: ''
        },
        refunded: {
          value_from: '',
          value_to: '',
          actual_to: '',
          actual_from: ''
        },
        due: {
          value_from: '',
          value_to: '',
          actual_to: '',
          actual_from: ''
        },
        status: {
          value: '',
          actual: ''
        },
        transaction_date: {
          value_to: '',
          value_from: '',
          actual_to: '',
          actual_from: ''
        },
        purchase_name: {
          value: '',
          actual: ''
        },
        purchase_type: {
          value: '',
          actual: ''
        },
        quantity: {
          value_to: '',
          value_from: '',
          actual_to: '',
          actual_from: '',
          unlimited: false
        },
        purchase_time: {
          value_to: '',
          value_from: '',
          actual_to: '',
          actual_from: ''
        },
        tip_date: {
          value_to: '',
          value_from: '',
          actual_to: '',
          actual_from: ''
        },
        response_time: {
          value_to: '',
          value_from: '',
          actual_to: '',
          actual_from: ''
        },
        custom_field: {
          value: '',
          actual: ''
        },
        response: {
          value: '',
          actual: ''
        },
        custom_field_type: {
          value: '',
          actual: ''
        },
        // Resources (type 11)
        resource_type: {
          value: '',
          actual: ''
        },
        resource_category: {
          value: '',
          actual: ''
        },
        resource_name: {
          value: '',
          actual: ''
        },
        capacity: {
          value_from: '',
          value_to: '',
          actual_to: '',
          actual_from: ''
        },
        availability_start_date: {
          value_to: '',
          value_from: '',
          actual_to: '',
          actual_from: ''
        },
        availability_end_date: {
          value_to: '',
          value_from: '',
          actual_to: '',
          actual_from: ''
        },
        availability_start_time: {
          value_to: '',
          value_from: '',
          actual_to: '',
          actual_from: ''
        },
        availability_end_time: {
          value_to: '',
          value_from: '',
          actual_to: '',
          actual_from: ''
        },
        available_hours: {
          value_from: '',
          value_to: '',
          actual_to: '',
          actual_from: ''
        },
        booked_hours: {
          value_from: '',
          value_to: '',
          actual_to: '',
          actual_from: ''
        },
        idle_hours: {
          value_from: '',
          value_to: '',
          actual_to: '',
          actual_from: ''
        },
        utilization: {
          value_from: '',
          value_to: '',
          actual_to: '',
          actual_from: ''
        },
        bookings_assigned: {
          value_from: '',
          value_to: '',
          actual_to: '',
          actual_from: ''
        },
        // Staff activity (type 12)
        staff_members: {
          value: '',
          actual: ''
        },
        staff_role: {
          value: '',
          actual: ''
        },
        staff_email: {
          value: '',
          actual: ''
        },
        event_name: {
          value: '',
          actual: ''
        },
        event_date: {
          value_to: '',
          value_from: '',
          actual_to: '',
          actual_from: ''
        },
        event_time: {
          value_to: '',
          value_from: '',
          actual_to: '',
          actual_from: ''
        },
        involvement: {
          value: '',
          actual: ''
        },
        order_number: {
          value: '',
          actual: ''
        },
        tips_collected: {
          value_from: '',
          value_to: '',
          actual_to: '',
          actual_from: ''
        },
        // Waivers (type 13). Shared keys (first_name, last_name, email, phone, birthday, city,
        // state, zip, referral_source, player_experience_level, group_name, event_name,
        // event_date, event_time, order_number) are defined above and reused.
        is_minor: { value: '', actual: '' },
        waiver_type: { value: '', actual: '' },
        signed_date: { value_to: '', value_from: '', actual_to: '', actual_from: '' },
        signature_on_file: { value: '', actual: '' },
        photo_opt_out: { value: '', actual: '' },
        email_opt_out: { value: '', actual: '' },
        not_participating: { value: '', actual: '' },
        created_internally: { value: '', actual: '' },
        guardian_email: { value: '', actual: '' },
        guardian_phone: { value: '', actual: '' },
        // Checklist runs (type 14) and Checklist steps (type 15). Shared keys group_name,
        // event_name, event_date, event_time, tag, ticket_key are defined above and reused.
        run_id: { value_to: '', value_from: '', actual_to: '', actual_from: '' },
        checklist_name: { value: '', actual: '' },
        checklist_archived: { value: '', actual: '' },
        event_host: { value: '', actual: '' },
        started_by: { value: '', actual: '' },
        completed_by: { value: '', actual: '' },
        started_at: { value_to: '', value_from: '', actual_to: '', actual_from: '' },
        completed_at: { value_to: '', value_from: '', actual_to: '', actual_from: '' },
        duration_minutes: { value_to: '', value_from: '', actual_to: '', actual_from: '' },
        steps_total: { value_to: '', value_from: '', actual_to: '', actual_from: '' },
        steps_done: { value_to: '', value_from: '', actual_to: '', actual_from: '' },
        percent_complete: { value_to: '', value_from: '', actual_to: '', actual_from: '' },
        issues_flagged: { value_to: '', value_from: '', actual_to: '', actual_from: '' },
        steps_with_notes: { value_to: '', value_from: '', actual_to: '', actual_from: '' },
        steps_with_photos: { value_to: '', value_from: '', actual_to: '', actual_from: '' },
        tickets_created: { value_to: '', value_from: '', actual_to: '', actual_from: '' },
        run_status: { value: '', actual: '' },
        requires_approval: { value: '', actual: '' },
        reviewed_by: { value: '', actual: '' },
        reviewed_at: { value_to: '', value_from: '', actual_to: '', actual_from: '' },
        review_note: { value: '', actual: '' },
        run_tag: { value: '', actual: '' },
        step_number: { value_to: '', value_from: '', actual_to: '', actual_from: '' },
        step_name: { value: '', actual: '' },
        step_required: { value: '', actual: '' },
        response_type: { value: '', actual: '' },
        step_done: { value: '', actual: '' },
        done_by: { value: '', actual: '' },
        done_at: { value_to: '', value_from: '', actual_to: '', actual_from: '' },
        minutes_from_start: { value_to: '', value_from: '', actual_to: '', actual_from: '' },
        step_response: { value: '', actual: '' },
        step_note: { value: '', actual: '' },
        issue_flagged: { value: '', actual: '' },
        issue_description: { value: '', actual: '' },
        photo_count: { value_to: '', value_from: '', actual_to: '', actual_from: '' },
        step_tag: { value: '', actual: '' },
      }
    }
  },
  computed: {
    // FilterBar schema for the current report: the master filter list, filtered to those
    // whose backing field is selected in this report (mirrors the old fieldExists() v-show).
    filterSchema() {
      return this.allFilterDefs().filter(f => this._isGateOpen(f._gate))
    },
    downloadPhaseLabel() {
      if (this.cancelling) return 'Cancelling your download'
      if (this.downloadPhase === 'preparing') return 'Preparing your report'
      return 'Downloading'
    },
    downloadRowsLabel() {
      const fetched = this.rowsFetched.toLocaleString()
      const total = this.totalRows ? this.totalRows.toLocaleString() : '?'
      return `${fetched} of ${total} rows`
    },
    downloadEtaLabel() {
      if (!this.downloadStartedAt || !this.rowsFetched || !this.totalRows) return ''
      const elapsedMs = Date.now() - this.downloadStartedAt
      const percent = this.rowsFetched / this.totalRows
      if (percent < 0.05) return ''
      const remainingMs = (elapsedMs / this.rowsFetched) * (this.totalRows - this.rowsFetched)
      const remainingSec = Math.max(1, Math.round(remainingMs / 1000))
      if (remainingSec < 60) return `About ${remainingSec}s remaining`
      const min = Math.floor(remainingSec / 60)
      const sec = remainingSec % 60
      return `About ${min}m ${sec}s remaining`
    },
    presetDirty() {
      return JSON.stringify(this.presetSnapshot()) !== JSON.stringify(this.presetFilters || {})
    },
    summaryModeValue: {
      get() { return this.summaryActive ? 'summary' : 'detail' },
      set(v) {
        this.summaryActive = v === 'summary'
        if(this.summaryActive && !this.summaryGroupBy){
          const first = this.summaryGroupOptions[0]
          this.summaryGroupBy = first ? first.field : null
        }
        // The other mode has a different column set, so its sort can't carry over.
        this.sortBy = ''
        this.sortDesc = false
        this.currentPage = 1
        this.generateReport()
      }
    },
    summaryGroupOptions() {
      return (this.fieldMeta || []).filter(f => f.data_type !== 'money')
    },
    summaryGroupByIsDate() {
      const f = (this.fieldMeta || []).find(o => o.field === this.summaryGroupBy)
      return !!f && f.data_type === 'date'
    },
    summaryGroupByIsTime() {
      const f = (this.fieldMeta || []).find(o => o.field === this.summaryGroupBy)
      return !!f && f.data_type === 'time'
    },
    summaryBucketOptions() {
      return [
        { value: 'day', label: 'Day' },
        { value: 'week', label: 'Week' },
        { value: 'month', label: 'Month' },
        { value: 'quarter', label: 'Quarter' },
        { value: 'year', label: 'Year' }
      ]
    },
    // b-table display fields: same keys as this.fields (which gates/CSV still use as
    // strings), with venue-facing labels (game -> event). In summary mode the columns
    // are the group value, the row count, and one total per money/numeric field;
    // formatters apply because no cell() slot matches these keys.
    tableFields() {
      if(this.summaryActive && this.data.length > 0 && this.data[0].summary_group !== undefined){
        let groupLabel = this.fieldLabel(this.summaryGroupBy)
        if(this.summaryGroupByIsDate){ groupLabel += ' (' + this.summaryBucket + ')' }
        if(this.summaryGroupByIsTime){ groupLabel += ' (hour)' }
        const cols = [
          { key: 'summary_group', label: groupLabel, sortable: true, formatter: v => this.formatSummaryGroup(v) },
          { key: 'row_count', label: 'Rows', sortable: true }
        ]
        for(const f of this.fieldMeta){
          const key = 'sum_' + f.field
          if(this.data[0][key] !== undefined){
            cols.push({
              key,
              label: 'Total ' + this.fieldLabel(f.field).toLowerCase(),
              sortable: true,
              formatter: f.data_type === 'money'
                ? v => this.getAmount(v || 0)
                : v => v === null ? '-' : v
            })
          }
        }
        return cols
      }
      // Empty values show as '-' on screen (placeholder is display-only; the CSV export
      // writes blanks). Cell slots that read row.item directly are unaffected by this
      // formatter and already render '-' themselves.
      return this.fields.map(f => {
        const words = String(f).replaceAll('_', ' ')
        return {
          key: f,
          label: gameToEventLabel(words.charAt(0).toUpperCase() + words.slice(1)),
          sortable: true,
          formatter: v => (v === null || v === undefined || v === '') ? '-' : v
        }
      })
    },
    presetQuickActions() {
      if (!this.firstLoadDone || !this.canEditReport || !this.presetDirty) return []
      return [{ key: 'save_preset', label: 'Save filtered view', end: true, solid: true }]
    },
  },
  methods: {
    onSummaryConfigChange(){
      this.sortBy = ''
      this.sortDesc = false
      this.currentPage = 1
      this.generateReport()
    },
    onSortChanged(ctx){
      // Server-side sort: b-table only holds the current page, so reordering must
      // come from a refetch. Reset to page 1 so the new top-of-sort row is visible.
      this.sortBy = ctx.sortBy || ''
      this.sortDesc = !!ctx.sortDesc
      this.currentPage = 1
      this.generateReport()
    },
    formatUtmValue(v){ return formatUtmValue(v) },
    fieldLabel(f){
      const words = String(f).replaceAll('_', ' ')
      return gameToEventLabel(words.charAt(0).toUpperCase() + words.slice(1))
    },
    formatSummaryGroup(v){
      if(v === null || v === undefined || v === ''){ return '-' }
      if(['source', 'medium', 'campaign'].includes(this.summaryGroupBy)){ return formatUtmValue(v) || '-' }
      if(String(v).includes('0000-00')){ return '-' }
      if(this.summaryGroupByIsTime){
        return this.getTime(v)
      }
      if(this.summaryGroupByIsDate && (this.summaryBucket === 'day' || this.summaryBucket === 'week')){
        return this.getFormattedDate(v)
      }
      // Safety net: a group value that is a raw datetime (a date-typed column that
      // slipped through as categorical) renders as date + time in the group format
      // instead of the ISO string.
      if(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(String(v))){
        return this.getFormattedDate(v) + ', ' + this.getTime(v)
      }
      return v
    },
    // ── FilterBar migration ──────────────────────────────────────────────────
    // Master list of every report filter. `_gate` decides visibility (mirrors the old
    // fieldExists() v-show); `_model` tells onFiltersChanged how to translate the FilterBar
    // value back into the internal `filters` object that buildUrl()/getAllData() still read.
    allFilterDefs() {
      const opt = (coll, vf) => (this[coll] || []).map(o => ({ label: o.name, value: vf ? o[vf] : o.id }))
      const yn = [{ label: 'Yes', value: '1' }, { label: 'No', value: '0' }]
      return [
        // Customers (type 1)
        { key: 'group_name', type: 'multiselect', label: 'Group', primary: true, options: opt('groups'), _gate: 'group_name', _model: 'multi-id', _coll: 'groups' },
        { key: 'first_name', type: 'text', label: 'First name', operatorLabel: 'contains', primary: true, _gate: 'first_name', _model: 'text' },
        { key: 'last_name', type: 'text', label: 'Last name', operatorLabel: 'contains', primary: true, _gate: 'last_name', _model: 'text' },
        { key: 'email', type: 'text', label: 'Email', operatorLabel: 'contains', _gate: 'email', _model: 'text' },
        { key: 'phone', type: 'text', label: 'Phone', operatorLabel: 'contains', _gate: 'phone', _model: 'text' },
        { key: 'address', type: 'text', label: 'Address', operatorLabel: 'contains', _gate: ['address_line_1', 'address_line_2'], _model: 'text' },
        { key: 'city', type: 'text', label: 'City', operatorLabel: 'contains', _gate: 'city', _model: 'text' },
        { key: 'state', type: 'text', label: 'State', operatorLabel: 'contains', _gate: 'state', _model: 'text' },
        { key: 'zip', type: 'text', label: 'Zip', operatorLabel: 'contains', _gate: 'zip', _model: 'text' },
        { key: 'country', type: 'text', label: 'Country', operatorLabel: 'contains', _gate: 'country', _model: 'text' },
        { key: 'referral_source', type: 'text', label: 'Referral source', operatorLabel: 'contains', _gate: 'referral_source', _model: 'text' },
        { key: 'player_experience_level', type: 'text', label: 'Player experience level', operatorLabel: 'contains', _gate: 'player_experience_level', _model: 'text' },
        { key: 'birthday', type: 'daterange', label: 'Birthday', _gate: 'birthday', _model: 'daterange' },
        { key: 'date_joined', type: 'daterange', label: 'Date joined', primary: true, _gate: 'date_joined', _model: 'daterange' },
        { key: 'date_last_visited', type: 'daterange', label: 'Date last visited', _gate: 'date_last_visited', _model: 'daterange' },
        { key: 'opted_out_of_receiving_emails', type: 'select', label: 'Opted out of emails', options: yn, _gate: 'opted_out_of_receiving_emails', _model: 'select' },
        { key: 'opted_out_of_posting_photos', type: 'select', label: 'Opted out of photos', options: yn, _gate: 'opted_out_of_posting_photos', _model: 'select' },
        { key: 'tag', type: 'text', label: 'Tag', operatorLabel: 'contains', _gate: 'tags', _model: 'text' },
        // Games (type 2)
        { key: 'start_time', type: 'timerange', label: 'Start time', _gate: () => this.fieldExists('start_time') && this.report.report_type_id === 2, _model: 'timerange' },
        { key: 'end_time', type: 'timerange', label: 'End time', _gate: () => this.fieldExists('end_time') && this.report.report_type_id === 2, _model: 'timerange' },
        { key: 'game_name', type: 'multiselect', label: 'Event', primary: true, options: opt('games'), _gate: 'game_name', _model: 'multi-id', _coll: 'games' },
        { key: 'team_experience_level', type: 'select', label: 'Team experience level', options: [{ label: 'New', value: '1' }, { label: 'Some have played before', value: '2' }, { label: 'Experienced', value: '3' }, { label: 'Expert', value: '4' }], _gate: 'team_experience_level', _model: 'select' },
        { key: 'pre_game_notes', type: 'text', label: 'Pre-event notes', operatorLabel: 'contains', _gate: 'pre_game_notes', _model: 'text' },
        { key: 'post_game_notes', type: 'text', label: 'Post-event notes', operatorLabel: 'contains', _gate: 'post_game_notes', _model: 'text' },
        { key: 'completed', type: 'select', label: 'Completed', options: yn, _gate: 'completed', _model: 'select' },
        { key: 'game_master', type: 'multiselect', label: 'Event host', options: opt('company_users'), _gate: 'game_master', _model: 'multi-id', _coll: 'company_users' },
        { key: 'booking_date', type: 'daterange', label: 'Booking date', primary: true, _gate: 'booking_date', _model: 'daterange' },
        { key: 'booking_time', type: 'timerange', label: 'Booking time', _gate: 'booking_time', _model: 'timerange' },
        { key: 'number_of_hints', type: 'numberrange', label: 'Number of hints', _gate: 'number_of_hints', _model: 'numberrange' },
        { key: 'number_of_breaks', type: 'numberrange', label: 'Number of breaks', _gate: 'number_of_breaks', _model: 'numberrange' },
        // Transactions (type 3)
        { key: 'transaction_id', type: 'text', label: 'Order number', operatorLabel: 'contains', primary: true, _gate: 'transaction_id', _model: 'text' },
        { key: 'customer_first_name', type: 'text', label: 'Customer first name', operatorLabel: 'contains', primary: true, _gate: 'customer_first_name', _model: 'text' },
        { key: 'customer_last_name', type: 'text', label: 'Customer last name', operatorLabel: 'contains', primary: true, _gate: 'customer_last_name', _model: 'text' },
        { key: 'customer_email', type: 'text', label: 'Customer email', operatorLabel: 'contains', _gate: 'customer_email', _model: 'text' },
        { key: 'customer_phone', type: 'text', label: 'Customer phone', operatorLabel: 'contains', _gate: 'customer_phone', _model: 'text' },
        { key: 'customer_address', type: 'text', label: 'Customer address', operatorLabel: 'contains', _gate: 'customer_address', _model: 'text' },
        { key: 'customer_city', type: 'text', label: 'Customer city', operatorLabel: 'contains', _gate: 'customer_city', _model: 'text' },
        { key: 'customer_state', type: 'text', label: 'Customer state', operatorLabel: 'contains', _gate: 'customer_state', _model: 'text' },
        { key: 'customer_zip', type: 'text', label: 'Customer zip', operatorLabel: 'contains', _gate: 'customer_zip', _model: 'text' },
        { key: 'customer_credit', type: 'text', label: 'Customer credit', _gate: 'customer_credit', _model: 'text' },
        { key: 'price', type: 'numberrange', label: 'Price', _gate: 'price', _model: 'numberrange' },
        { key: 'fee', type: 'numberrange', label: 'Fee', _gate: 'fee', _model: 'numberrange' },
        { key: 'discount', type: 'numberrange', label: 'Discount', _gate: 'discount', _model: 'numberrange' },
        { key: 'tax', type: 'numberrange', label: 'Tax', _gate: 'tax', _model: 'numberrange' },
        { key: 'tips', type: 'numberrange', label: 'Tips', _gate: 'tips', _model: 'numberrange' },
        { key: 'tip_date', type: 'daterange', label: 'Tip date', _gate: 'tip_date', _model: 'daterange' },
        { key: 'tip_assignees', type: 'text', label: 'Tip assignees', operatorLabel: 'contains', _gate: 'tip_assignees', _model: 'text' },
        { key: 'transaction_notes', type: 'text', label: 'Transaction notes', operatorLabel: 'contains', _gate: 'transaction_notes', _model: 'text' },
        { key: 'tax_names', type: 'text', label: 'Tax names', operatorLabel: 'contains', _gate: 'tax_names', _model: 'text' },
        { key: 'total', type: 'numberrange', label: 'Total', _gate: 'total', _model: 'numberrange' },
        { key: 'paid', type: 'numberrange', label: 'Paid', _gate: 'paid', _model: 'numberrange' },
        { key: 'refunded', type: 'numberrange', label: 'Refunded', _gate: 'refunded', _model: 'numberrange' },
        { key: 'due', type: 'numberrange', label: 'Due', _gate: 'due', _model: 'numberrange' },
        { key: 'status', type: 'select', label: 'Status', primary: true, options: [{ label: 'Active', value: '1' }, { label: 'Cancelled', value: '0' }], _gate: () => this.fieldExists('status') && this.report.report_type_id !== 9, _model: 'select' },
        { key: 'transaction_date', type: 'daterange', label: 'Transaction date', primary: true, _gate: 'transaction_date', _model: 'daterange' },
        { key: 'transaction_has_due_amount', type: 'select', label: 'Has due amount', options: yn, _gate: 'transaction_has_due_amount', _model: 'select' },
        { key: 'source', type: 'text', label: 'Source', operatorLabel: 'contains', _gate: 'source', _model: 'text' },
        { key: 'medium', type: 'text', label: 'Medium', operatorLabel: 'contains', _gate: 'medium', _model: 'text' },
        { key: 'campaign', type: 'text', label: 'Campaign', operatorLabel: 'contains', _gate: 'campaign', _model: 'text' },
        { key: 'referrer', type: 'text', label: 'Referrer', operatorLabel: 'contains', _gate: 'referrer', _model: 'text' },
        { key: 'traffic_attribution', type: 'text', label: 'Traffic attribution', operatorLabel: 'contains', _gate: 'traffic_attribution', _model: 'text' },
        { key: 'promo_codes', type: 'selecttext', label: 'Promo code', operatorLabel: 'contains', textOption: '2', options: [{ label: 'Has any promo code', value: '1' }, { label: 'No promo code', value: '0' }, { label: 'Search by promo code', value: '2' }], _gate: 'promo_codes', _model: 'promo' },
        // Purchases (type 4)
        { key: 'purchase_name', type: 'multiselect', label: 'Purchase', primary: true, options: (this.purchases || []).map((o, idx) => ({ label: o.name, value: idx })), _gate: ['purchase_name', 'purchases_in_transaction'], _model: 'multi-purchase', _coll: 'purchases' },
        { key: 'purchase_type', type: 'multiselect', label: 'Purchase type', options: opt('purchase_types', 'name'), _gate: 'purchase_type', _model: 'multi-name' },
        { key: 'quantity', type: 'numberrange', label: 'Quantity', allowUnlimited: true, _gate: 'quantity', _model: 'numberrange' },
        { key: 'purchase_time', type: 'daterange', label: 'Purchase time', _gate: 'purchase_time', _model: 'daterange' },
        // Payments (type 6)
        { key: 'payment_methods', type: 'text', label: 'Payment method', operatorLabel: 'contains', primary: true, _gate: 'payment_method', _model: 'text' },
        { key: 'card_type', type: 'text', label: 'Card type', operatorLabel: 'contains', primary: true, _gate: 'card_type', _model: 'text' },
        { key: 'last_four', type: 'text', label: 'Last four', operatorLabel: 'contains', _gate: 'last_four', _model: 'text' },
        { key: 'expiry', type: 'text', label: 'Expiry', operatorLabel: 'contains', _gate: 'expiry', _model: 'text' },
        { key: 'payment_gateway_id', type: 'text', label: 'Gateway transaction', operatorLabel: 'contains', _gate: 'payment_gateway_transaction_id', _model: 'text' },
        { key: 'payment_collected_by', type: 'multiselect', label: 'Collected by', primary: true, options: opt('company_users'), _gate: 'payment_collected_by', _model: 'multi-id', _coll: 'company_users' },
        // Promo codes (type 7)
        { key: 'promo_code_name', type: 'multiselect', label: 'Promo code', primary: true, options: opt('promo_codes'), _gate: 'promo_code_name', _model: 'multi-id', _coll: 'promo_codes' },
        { key: 'description', type: 'text', label: 'Description', operatorLabel: 'contains', _gate: 'description', _model: 'text' },
        { key: 'redemption_type', type: 'text', label: 'Redemption type', operatorLabel: 'contains', _gate: 'redemption_type', _model: 'text' },
        { key: 'value_type', type: 'text', label: 'Value type', operatorLabel: 'contains', _gate: 'value_type', _model: 'text' },
        { key: 'value_amount', type: 'numberrange', label: 'Value amount', _gate: 'value_amount', _model: 'numberrange' },
        { key: 'can_be_combined', type: 'select', label: 'Can be combined', options: yn, _gate: 'can_be_combined', _model: 'select' },
        { key: 'issued_by_campaign', type: 'select', label: 'Issued by campaign', options: yn, _gate: 'issued_by_campaign', _model: 'select' },
        { key: 'number_of_times_used', type: 'numberrange', label: 'Times used', _gate: 'number_of_times_used', _model: 'numberrange' },
        { key: 'applicable_to_miscellaneous_items', type: 'select', label: 'Applies to misc items', options: yn, _gate: 'applicable_to_miscellaneous_items', _model: 'select' },
        { key: 'internal_only', type: 'select', label: 'Internal only', options: yn, _gate: 'internal_only', _model: 'select' },
        { key: 'games', type: 'multiselect', label: 'Games', options: opt('games', 'name'), _gate: 'games', _model: 'multi-name' },
        // Custom fields (type 8)
        { key: 'custom_field', type: 'multiselect', label: 'Custom field', primary: true, options: opt('custom_fields'), _gate: 'custom_field', _model: 'multi-id', _coll: 'custom_fields' },
        { key: 'custom_field_type', type: 'text', label: 'Custom field type', operatorLabel: 'contains', _gate: 'custom_field_type', _model: 'text' },
        { key: 'response', type: 'text', label: 'Response', operatorLabel: 'contains', _gate: 'response', _model: 'text' },
        { key: 'response_time', type: 'daterange', label: 'Response date', _gate: 'response_time', _model: 'daterange' },
        // Project management — tickets (type 9)
        { key: 'projects', type: 'multiselect', label: 'Project', primary: true, options: opt('projects'), _gate: 'project_name', _model: 'multi-id', _coll: 'projects' },
        { key: 'statuses', type: 'multiselect', label: 'Status', primary: true, options: opt('ticket_statuses'), _gate: () => this.fieldExists('status') && this.report.report_type_id === 9, _model: 'multi-id', _coll: 'ticket_statuses' },
        { key: 'priorities', type: 'multiselect', label: 'Priority', primary: true, options: [{ label: 'Highest', value: 1 }, { label: 'High', value: 2 }, { label: 'Medium', value: 3 }, { label: 'Low', value: 4 }, { label: 'Lowest', value: 5 }], _gate: 'priority', _model: 'multi-raw' },
        { key: 'assignees', type: 'multiselect', label: 'Assignee', options: opt('team_members'), _gate: 'assignee', _model: 'multi-id', _coll: 'team_members' },
        { key: 'reporters', type: 'multiselect', label: 'Reporter', options: opt('team_members'), _gate: 'reporter', _model: 'multi-id', _coll: 'team_members' },
        { key: 'title', type: 'text', label: 'Title', operatorLabel: 'contains', primary: true, _gate: 'title', _model: 'text' },
        { key: 'ticket_key', type: 'text', label: 'Ticket key', operatorLabel: 'contains', _gate: 'ticket_key', _model: 'text' },
        { key: 'due_date', type: 'daterange', label: 'Due date', _gate: 'due_date', _model: 'daterange' },
        { key: 'resolved_date', type: 'daterange', label: 'Resolved date', _gate: 'resolved_date', _model: 'daterange' },
        // Time tracking (type 10)
        { key: 'users', type: 'multiselect', label: 'User', primary: true, options: opt('team_members'), _gate: 'user', _model: 'multi-id', _coll: 'team_members' },
        { key: 'categories', type: 'multiselect', label: 'Category', options: opt('time_log_categories'), _gate: 'category', _model: 'multi-id', _coll: 'time_log_categories' },
        { key: 'is_running', type: 'select', label: 'Running', options: [{ label: 'Running', value: '1' }, { label: 'Stopped', value: '0' }], _gate: 'is_running', _model: 'select' },
        { key: 'location_checks', type: 'multiselect', label: 'Location check', options: [{ label: 'On site', value: 'passed' }, { label: 'Off site', value: 'outside' }, { label: 'No location', value: 'unavailable' }, { label: 'Manual', value: 'manual' }, { label: 'Not required', value: 'not_required' }], _gate: 'location_check', _model: 'multi-raw' },
        { key: 'meters_from_venue', type: 'numberrange', label: 'Meters from venue', _gate: 'meters_from_venue', _model: 'numberrange' },
        // Resources (type 11) — each filter shows only when its column is in the report
        { key: 'resource_name', type: 'text', label: 'Resource name', operatorLabel: 'contains', primary: true, _gate: 'resource_name', _model: 'text' },
        { key: 'resource_type', type: 'select', label: 'Resource type', primary: true, options: [{ label: 'Staff', value: 'staff' }, { label: 'Resource', value: 'resource' }], _gate: 'resource_type', _model: 'select' },
        { key: 'resource_category', type: 'multiselect', label: 'Category', primary: true, options: opt('resource_categories'), _gate: 'resource_category', _model: 'multi-id', _coll: 'resource_categories' },
        { key: 'capacity', type: 'numberrange', label: 'Capacity', primary: true, _gate: 'capacity', _model: 'numberrange' },
        { key: 'availability_start_date', type: 'daterange', label: 'Availability start date', _gate: 'availability_start_date', _model: 'daterange' },
        { key: 'availability_end_date', type: 'daterange', label: 'Availability end date', _gate: 'availability_end_date', _model: 'daterange' },
        { key: 'availability_start_time', type: 'timerange', label: 'Availability start time', _gate: 'availability_start_time', _model: 'timerange' },
        { key: 'availability_end_time', type: 'timerange', label: 'Availability end time', _gate: 'availability_end_time', _model: 'timerange' },
        { key: 'available_hours', type: 'numberrange', label: 'Available hours', primary: true, _gate: 'available_hours', _model: 'numberrange' },
        { key: 'booked_hours', type: 'numberrange', label: 'Booked hours', primary: true, _gate: 'booked_hours', _model: 'numberrange' },
        { key: 'idle_hours', type: 'numberrange', label: 'Idle hours', primary: true, _gate: 'idle_hours', _model: 'numberrange' },
        { key: 'utilization', type: 'numberrange', label: 'Utilization', primary: true, _gate: 'utilization', _model: 'numberrange' },
        { key: 'bookings_assigned', type: 'numberrange', label: 'Bookings assigned', _gate: 'bookings_assigned', _model: 'numberrange' },
        // Staff activity (type 12) — each filter shows only when its column is in the report
        { key: 'staff_members', type: 'multiselect', label: 'Staff member', primary: true, options: opt('company_users'), _gate: 'staff_name', _model: 'multi-id', _coll: 'company_users' },
        { key: 'staff_role', type: 'text', label: 'Role', operatorLabel: 'contains', _gate: 'staff_role', _model: 'text' },
        { key: 'staff_email', type: 'text', label: 'Staff email', operatorLabel: 'contains', _gate: 'staff_email', _model: 'text' },
        { key: 'event_name', type: 'multiselect', label: 'Event', primary: true, options: opt('games'), _gate: 'event_name', _model: 'multi-id', _coll: 'games' },
        { key: 'event_date', type: 'daterange', label: 'Event date', primary: true, _gate: 'event_date', _model: 'daterange' },
        { key: 'event_time', type: 'timerange', label: 'Event time', _gate: 'event_time', _model: 'timerange' },
        { key: 'involvement', type: 'select', label: 'Involvement', primary: true, options: [{ label: 'Assigned', value: 'assigned' }, { label: 'Hosted', value: 'hosted' }, { label: 'Via resource', value: 'via_resource' }, { label: 'Received tip', value: 'tipped' }], _gate: 'involvement', _model: 'select' },
        { key: 'order_number', type: 'text', label: 'Order number', operatorLabel: 'contains', _gate: 'order_number', _model: 'text' },
        { key: 'tips_collected', type: 'numberrange', label: 'Tips collected', _gate: 'tips_collected', _model: 'numberrange' },
        // Waivers (type 13). The customer and staff rows above already cover the shared columns.
        { key: 'is_minor', type: 'select', label: 'Is minor', primary: true, options: yn, _gate: 'is_minor', _model: 'select' },
        { key: 'waiver_type', type: 'select', label: 'Waiver type', primary: true, options: [{ label: 'Booked', value: 'booked' }, { label: 'Walk-in', value: 'walkin' }], _gate: 'waiver_type', _model: 'select' },
        { key: 'signed_date', type: 'daterange', label: 'Signed date', primary: true, _gate: 'signed_date', _model: 'daterange' },
        { key: 'signature_on_file', type: 'select', label: 'Signature on file', options: yn, _gate: 'signature_on_file', _model: 'select' },
        { key: 'photo_opt_out', type: 'select', label: 'Photo opt out', options: yn, _gate: 'photo_opt_out', _model: 'select' },
        { key: 'email_opt_out', type: 'select', label: 'Email opt out', options: yn, _gate: 'email_opt_out', _model: 'select' },
        { key: 'not_participating', type: 'select', label: 'Not participating', options: yn, _gate: 'not_participating', _model: 'select' },
        { key: 'created_internally', type: 'select', label: 'Created internally', options: yn, _gate: 'created_internally', _model: 'select' },
        { key: 'guardian_email', type: 'text', label: 'Guardian email', operatorLabel: 'contains', _gate: 'guardian_email', _model: 'text' },
        { key: 'guardian_phone', type: 'text', label: 'Guardian phone', operatorLabel: 'contains', _gate: 'guardian_phone', _model: 'text' },
        // Checklist runs (type 14) and Checklist steps (type 15). group_name, event_name,
        // event_date, event_time, tag, and ticket_key rows above cover the shared columns.
        { key: 'run_id', type: 'numberrange', label: 'Run id', _gate: 'run_id', _model: 'numberrange' },
        { key: 'checklist_name', type: 'multiselect', label: 'Checklist', primary: true, options: opt('checklists'), _gate: 'checklist_name', _model: 'multi-id', _coll: 'checklists' },
        { key: 'checklist_archived', type: 'select', label: 'Checklist archived', options: yn, _gate: 'checklist_archived', _model: 'select' },
        { key: 'event_host', type: 'multiselect', label: 'Event host', options: opt('company_users'), _gate: 'event_host', _model: 'multi-id', _coll: 'company_users' },
        { key: 'started_by', type: 'multiselect', label: 'Started by', options: opt('company_users'), _gate: 'started_by', _model: 'multi-id', _coll: 'company_users' },
        { key: 'completed_by', type: 'multiselect', label: 'Completed by', primary: true, options: opt('company_users'), _gate: 'completed_by', _model: 'multi-id', _coll: 'company_users' },
        { key: 'started_at', type: 'daterange', label: 'Started at', _gate: 'started_at', _model: 'daterange' },
        { key: 'completed_at', type: 'daterange', label: 'Completed at', primary: true, _gate: 'completed_at', _model: 'daterange' },
        { key: 'duration_minutes', type: 'numberrange', label: 'Duration minutes', _gate: 'duration_minutes', _model: 'numberrange' },
        { key: 'steps_total', type: 'numberrange', label: 'Steps total', _gate: 'steps_total', _model: 'numberrange' },
        { key: 'steps_done', type: 'numberrange', label: 'Steps done', _gate: 'steps_done', _model: 'numberrange' },
        { key: 'percent_complete', type: 'numberrange', label: 'Percent complete', _gate: 'percent_complete', _model: 'numberrange' },
        { key: 'issues_flagged', type: 'numberrange', label: 'Issues flagged', _gate: 'issues_flagged', _model: 'numberrange' },
        { key: 'steps_with_notes', type: 'numberrange', label: 'Steps with notes', _gate: 'steps_with_notes', _model: 'numberrange' },
        { key: 'steps_with_photos', type: 'numberrange', label: 'Steps with photos', _gate: 'steps_with_photos', _model: 'numberrange' },
        { key: 'tickets_created', type: 'numberrange', label: 'Tickets created', _gate: 'tickets_created', _model: 'numberrange' },
        { key: 'run_status', type: 'select', label: 'Run status', primary: true, options: [{ label: 'In progress', value: 'in_progress' }, { label: 'Completed', value: 'completed' }, { label: 'Awaiting approval', value: 'awaiting_approval' }, { label: 'Approved', value: 'approved' }, { label: 'Rejected', value: 'rejected' }], _gate: 'run_status', _model: 'select' },
        { key: 'requires_approval', type: 'select', label: 'Requires approval', options: yn, _gate: 'requires_approval', _model: 'select' },
        { key: 'reviewed_by', type: 'multiselect', label: 'Reviewed by', options: opt('company_users'), _gate: 'reviewed_by', _model: 'multi-id', _coll: 'company_users' },
        { key: 'reviewed_at', type: 'daterange', label: 'Reviewed at', _gate: 'reviewed_at', _model: 'daterange' },
        { key: 'review_note', type: 'text', label: 'Review note', operatorLabel: 'contains', _gate: 'review_note', _model: 'text' },
        { key: 'run_tag', type: 'text', label: 'Run tag', operatorLabel: 'contains', _gate: 'run_tags', _model: 'text' },
        { key: 'step_number', type: 'numberrange', label: 'Step number', _gate: 'step_number', _model: 'numberrange' },
        { key: 'step_name', type: 'text', label: 'Step name', operatorLabel: 'contains', primary: true, _gate: 'step_name', _model: 'text' },
        { key: 'step_required', type: 'select', label: 'Step required', options: yn, _gate: 'step_required', _model: 'select' },
        { key: 'response_type', type: 'select', label: 'Response type', options: [{ label: 'Basic', value: 'checkbox' }, { label: 'Dropdown', value: 'dropdown' }, { label: 'Multi-select', value: 'multiselect' }, { label: 'Text', value: 'text' }, { label: 'Number', value: 'number' }], _gate: 'response_type', _model: 'select' },
        { key: 'step_done', type: 'select', label: 'Step done', primary: true, options: yn, _gate: 'step_done', _model: 'select' },
        { key: 'done_by', type: 'multiselect', label: 'Done by', options: opt('company_users'), _gate: 'done_by', _model: 'multi-id', _coll: 'company_users' },
        { key: 'done_at', type: 'daterange', label: 'Done at', _gate: 'done_at', _model: 'daterange' },
        { key: 'minutes_from_start', type: 'numberrange', label: 'Minutes from start', _gate: 'minutes_from_start', _model: 'numberrange' },
        { key: 'step_response', type: 'text', label: 'Step response', operatorLabel: 'contains', _gate: 'step_response', _model: 'text' },
        { key: 'step_note', type: 'text', label: 'Step note', operatorLabel: 'contains', _gate: 'step_note', _model: 'text' },
        { key: 'issue_flagged', type: 'select', label: 'Issue flagged', primary: true, options: yn, _gate: 'issue_flagged', _model: 'select' },
        { key: 'issue_description', type: 'text', label: 'Issue description', operatorLabel: 'contains', _gate: 'issue_description', _model: 'text' },
        { key: 'photo_count', type: 'numberrange', label: 'Photo count', _gate: 'photo_count', _model: 'numberrange' },
        { key: 'step_tag', type: 'text', label: 'Step tag', operatorLabel: 'contains', _gate: 'step_tags', _model: 'text' },
      ]
    },
    _isGateOpen(gate) {
      if (typeof gate === 'function') return gate()
      if (Array.isArray(gate)) return gate.some(g => this.fieldExists(g))
      return this.fieldExists(gate)
    },
    // Convert a FilterBar daterange value ({mode,days,from,to}) into {from,to} as yyyy-MM-dd,
    // matching what the old el-date-picker filters produced (buildUrl reads these directly).
    _rangeDates(d) {
      if (!d) return { from: '', to: '' }
      const fmt = x => x.getFullYear() + '-' + ('0' + (x.getMonth() + 1)).slice(-2) + '-' + ('0' + x.getDate()).slice(-2)
      if (d.mode === 'days' && d.days !== '' && d.days != null) {
        const today = new Date()
        const dt = new Date()
        dt.setDate(dt.getDate() - Number(d.days))
        return { from: fmt(dt), to: fmt(today) }
      }
      if (d.mode === 'range') return { from: d.from || '', to: d.to || '' }
      return { from: '', to: '' }
    },
    buildInitialFilterValues() {
      const vals = {}
      for (const f of this.allFilterDefs()) {
        if (f.type === 'multiselect') vals[f.key] = []
        else if (f.type === 'daterange') vals[f.key] = { mode: 'range', days: '', from: '', to: '' }
        else if (f.type === 'numberrange') vals[f.key] = f.allowUnlimited ? { from: '', to: '', unlimited: false } : { from: '', to: '' }
        else if (f.type === 'timerange') vals[f.key] = { from: '', to: '' }
        else if (f.type === 'selecttext') vals[f.key] = { mode: '', text: '' }
        else vals[f.key] = ''
      }
      return vals
    },
    // Translate FilterBar's value snapshot into the internal `filters` model (no refetch).
    applyFilterValues(values) {
      for (const f of this.allFilterDefs()) {
        const v = values[f.key]
        if (f._model === 'promo') {
          // One control, three mutually exclusive modes matching the backend contract:
          // with_promo_code = '1' (has any) / '0' (none) / '2' (search by text). The text
          // LIKE only applies alongside with_promo_code = '2', so text rides only that mode.
          const pv = v || {}
          const mode = pv.mode == null ? '' : pv.mode
          const text = pv.text == null ? '' : pv.text
          const isTextMode = mode === f.textOption
          this.filters.promo_codes.value = isTextMode ? text : ''
          this.filters.promo_codes.actual = this.filters.promo_codes.value
          // A text mode with no text entered isn't a real filter, so send no mode either.
          this.filters.promo_codes.with_promo_code_value = (isTextMode && text === '') ? '' : mode
          this.filters.promo_codes.with_promo_code_actual = this.filters.promo_codes.with_promo_code_value
          continue
        }
        const tgt = this.filters[f.key]
        if (!tgt) continue
        if (f._model === 'text' || f._model === 'select') {
          tgt.value = v == null ? '' : v
          tgt.actual = tgt.value
        } else if (f._model === 'multi-id') {
          const arr = Array.isArray(v) ? v : []
          const coll = this[f._coll] || []
          tgt.value = arr.map(id => { const o = coll.find(x => x.id === id) || {}; return { id, name: o.name } })
          tgt.actual = tgt.value
        } else if (f._model === 'multi-purchase') {
          // purchases is a UNION of games/items/gift cards where ids collide, so options are
          // keyed by collection INDEX (unique); resolve each back to its own {id, name}.
          const arr = Array.isArray(v) ? v : []
          const coll = this.purchases || []
          tgt.value = arr.map(idx => { const o = coll[idx] || {}; return { id: o.id, name: o.name } }).filter(o => o.id != null)
          tgt.actual = tgt.value
        } else if (f._model === 'multi-name') {
          const arr = Array.isArray(v) ? v : []
          tgt.value = arr.map(name => ({ name }))
          tgt.actual = tgt.value
        } else if (f._model === 'multi-raw') {
          tgt.value = Array.isArray(v) ? [...v] : []
          tgt.actual = tgt.value
        } else if (f._model === 'daterange') {
          const r = this._rangeDates(v)
          tgt.value_from = r.from; tgt.value_to = r.to
          tgt.actual_from = r.from; tgt.actual_to = r.to
        } else if (f._model === 'numberrange') {
          const nv = v || {}
          tgt.value_from = nv.from == null ? '' : nv.from
          tgt.value_to = nv.to == null ? '' : nv.to
          tgt.actual_from = tgt.value_from; tgt.actual_to = tgt.value_to
          if (Object.prototype.hasOwnProperty.call(tgt, 'unlimited')) tgt.unlimited = !!nv.unlimited
        } else if (f._model === 'timerange') {
          const tv = v || {}
          tgt.value_from = tv.from || ''; tgt.value_to = tv.to || ''
          tgt.actual_from = tgt.value_from; tgt.actual_to = tgt.value_to
        }
      }
      this.filterValues = { ...this.filterValues, ...values }
    },
    onFiltersChanged(values) {
      this.applyFilterValues(values)
      this.currentPage = 1
      this.generateReport()
    },
    onPerPageChange(n) {
      this.perPage = n
      this.currentPage = 1
      this.generateReport()
    },
    onQuickAction(key) {
      if (key === 'save_preset' && !this.savingPreset) {
        this.savePresetFilters()
      }
    },
    cancelDownload() {
      this.downloadCancelled = true;
      this.cancelling = true;
    },
    hasPermissions(scope){
      let permissions = JSON.parse(localStorage.getItem('permissions'))
      const matchFound = permissions.some(element => element === scope);
      return matchFound
    },
    formatHours(h){
      if(h === null || h === undefined || h === '' || isNaN(h)){
        return '-'
      }
      return Number(h).toFixed(1)
    },
    formatUtilization(u){
      if(u === null || u === undefined || u === '' || isNaN(u)){
        return '-'
      }
      return Number(u) + '%'
    },
    getExpiry(dateString){
      if(!dateString){
        return '-'
      }
      const [year, month] = dateString.split('-');
      return `${month}/${year.slice(-2)}`;
    },
    getPaymentMethod(method){
      return method
        .charAt(0).toUpperCase() + method.slice(1)
        .replace(/_/g, ' ');
    },
    getGiftCardCode(code){
      if(!code){
        return
      }
      if(isNaN(code)){
        return code
      }
      let c = code.match(/.{1,4}/g);
      return c.join(' ')
    },
    fieldExists(field){
      return _.includes(this.fields,  field )
    },
    priorityLabel(p) {
      // work_tickets.priority is stored as a number 1-5
      return { 1: 'Highest', 2: 'High', 3: 'Medium', 4: 'Low', 5: 'Lowest' }[p] || (p == null ? '-' : p)
    },
    capitalizeFirstLetter(str) {
      if (str == null || typeof str !== 'string') return str;
      return str.charAt(0).toUpperCase() + str.slice(1);
    },
    capitalizeFirstLetterRemoveUnderscore(string){
      if (!string) {
        return string;
      }
      string = string.replace(/_/g, ' ');
      return string.charAt(0).toUpperCase() + string.slice(1);
    },
    decapitalizeFirstLetterAddUnderscore(string) {
      if (!string) {
        return string;
      }
      string = string.replace(/ /g, '_');
      return string.charAt(0).toLowerCase() + string.slice(1);
    },
    async getAllData(config, count){
      let url = ''
      let csvData = []
      let companyId = localStorage.getItem('companyId')
      let companyGroupId = JSON.parse(localStorage.getItem('group')).id
      let reportId = this.$route.query.id

      let offset = 0
      const maxRetries = 3;
      const minCount = 100
      const retryDelaysMs = [1000, 3000, 9000]
      while(offset <= this.totalRows){
        if(this.downloadCancelled) {
          return null; // Return null to indicate cancellation
        }
        url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/reports/' + reportId +
          '/run?offset=' + offset + `&count=` + count + `&skip_count=1`

        if(this.filters.group_name.actual != ''){
          let group_arr = []
          for(let n in this.filters.group_name.value){
            group_arr.push(this.filters.group_name.value[n].id)
          }
          url += '&groups=[' + encodeURIComponent(group_arr) + ']'
        }

        if(this.filters.resource_name.actual != ''){
          url += '&resource_name=' + encodeURIComponent(this.filters.resource_name.value)
        }
        if(this.filters.resource_type.actual != ''){
          url += '&resource_type=' + encodeURIComponent(this.filters.resource_type.value)
        }
        if(this.filters.resource_category.actual != '' && this.filters.resource_category.value.length > 0){
          let resource_category_arr = []
          for(let n in this.filters.resource_category.value){
            resource_category_arr.push(this.filters.resource_category.value[n].id)
          }
          url += '&resource_category=[' + encodeURIComponent(resource_category_arr) + ']'
        }
        if(this.filters.capacity.actual_from !== '') url += '&capacity_from=' + encodeURIComponent(this.filters.capacity.value_from)
        if(this.filters.capacity.actual_to !== '') url += '&capacity_to=' + encodeURIComponent(this.filters.capacity.value_to)
        if(this.filters.available_hours.actual_from !== '') url += '&available_hours_from=' + encodeURIComponent(this.filters.available_hours.value_from)
        if(this.filters.available_hours.actual_to !== '') url += '&available_hours_to=' + encodeURIComponent(this.filters.available_hours.value_to)
        if(this.filters.booked_hours.actual_from !== '') url += '&booked_hours_from=' + encodeURIComponent(this.filters.booked_hours.value_from)
        if(this.filters.booked_hours.actual_to !== '') url += '&booked_hours_to=' + encodeURIComponent(this.filters.booked_hours.value_to)
        if(this.filters.idle_hours.actual_from !== '') url += '&idle_hours_from=' + encodeURIComponent(this.filters.idle_hours.value_from)
        if(this.filters.idle_hours.actual_to !== '') url += '&idle_hours_to=' + encodeURIComponent(this.filters.idle_hours.value_to)
        if(this.filters.utilization.actual_from !== '') url += '&utilization_from=' + encodeURIComponent(this.filters.utilization.value_from)
        if(this.filters.utilization.actual_to !== '') url += '&utilization_to=' + encodeURIComponent(this.filters.utilization.value_to)
        if(this.filters.bookings_assigned.actual_from !== '') url += '&bookings_assigned_from=' + encodeURIComponent(this.filters.bookings_assigned.value_from)
        if(this.filters.bookings_assigned.actual_to !== '') url += '&bookings_assigned_to=' + encodeURIComponent(this.filters.bookings_assigned.value_to)
        if(this.filters.availability_start_date.actual_to !== '' && this.filters.availability_start_date.actual_from !== ''){
          url += '&availability_start_date_from=' + encodeURIComponent(this.filters.availability_start_date.value_from) + '&availability_start_date_to=' + encodeURIComponent(this.filters.availability_start_date.value_to)
        }
        if(this.filters.availability_end_date.actual_to !== '' && this.filters.availability_end_date.actual_from !== ''){
          url += '&availability_end_date_from=' + encodeURIComponent(this.filters.availability_end_date.value_from) + '&availability_end_date_to=' + encodeURIComponent(this.filters.availability_end_date.value_to)
        }
        if(this.filters.availability_start_time.actual_to !== '' && this.filters.availability_start_time.actual_from !== ''){
          url += '&availability_start_time_from=' + encodeURIComponent(this.filters.availability_start_time.value_from) + '&availability_start_time_to=' + encodeURIComponent(this.filters.availability_start_time.value_to)
        }
        if(this.filters.availability_end_time.actual_to !== '' && this.filters.availability_end_time.actual_from !== ''){
          url += '&availability_end_time_from=' + encodeURIComponent(this.filters.availability_end_time.value_from) + '&availability_end_time_to=' + encodeURIComponent(this.filters.availability_end_time.value_to)
        }

        // Staff activity (type 12)
        if(this.filters.staff_members.actual !== '' && this.filters.staff_members.actual.length > 0){
          let staff_arr = []
          for(let n in this.filters.staff_members.value){
            staff_arr.push(this.filters.staff_members.value[n].id)
          }
          url += '&staff_ids=[' + encodeURIComponent(staff_arr) + ']'
        }
        if(this.filters.staff_role.actual !== ''){
          url += '&staff_role=' + encodeURIComponent(this.filters.staff_role.value)
        }
        if(this.filters.staff_email.actual !== ''){
          url += '&staff_email=' + encodeURIComponent(this.filters.staff_email.value)
        }
        if(this.filters.event_name.actual !== '' && this.filters.event_name.actual.length > 0){
          let event_arr = []
          for(let n in this.filters.event_name.value){
            event_arr.push(this.filters.event_name.value[n].id)
          }
          url += '&event_names=[' + encodeURIComponent(event_arr) + ']'
        }
        if(this.filters.event_date.actual_to !== '' && this.filters.event_date.actual_from !== ''){
          url += '&event_date_from=' + encodeURIComponent(this.filters.event_date.value_from) + '&event_date_to=' + encodeURIComponent(this.filters.event_date.value_to)
        }
        if(this.filters.event_time.actual_to !== '' && this.filters.event_time.actual_from !== ''){
          url += '&event_time_from=' + encodeURIComponent(this.filters.event_time.value_from) + '&event_time_to=' + encodeURIComponent(this.filters.event_time.value_to)
        }
        if(this.filters.involvement.actual !== ''){
          url += '&involvement=' + encodeURIComponent(this.filters.involvement.value)
        }
        if(this.filters.order_number.actual !== ''){
          url += '&order_number=' + encodeURIComponent(this.filters.order_number.value)
        }
        if(this.filters.tips_collected.actual_from !== '') url += '&tips_collected_from=' + encodeURIComponent(this.filters.tips_collected.value_from)
        if(this.filters.tips_collected.actual_to !== '') url += '&tips_collected_to=' + encodeURIComponent(this.filters.tips_collected.value_to)

        // Waivers (type 13). Shared text and date filters ride the customer report's blocks above.
        const waiverSelectFilters = ['is_minor', 'waiver_type', 'signature_on_file', 'photo_opt_out',
          'email_opt_out', 'not_participating', 'created_internally']
        for(const wk of waiverSelectFilters){
          if(this.filters[wk].actual !== ''){
            url += '&' + wk + '=' + encodeURIComponent(this.filters[wk].value)
          }
        }
        if(this.filters.signed_date.actual_to !== '' && this.filters.signed_date.actual_from !== ''){
          url += '&signed_date_from=' + encodeURIComponent(this.filters.signed_date.value_from) + '&signed_date_to=' + encodeURIComponent(this.filters.signed_date.value_to)
        }
        for(const gk of ['guardian_email', 'guardian_phone']){
          if(this.filters[gk].actual !== ''){
            url += '&' + gk + '=' + encodeURIComponent(this.filters[gk].value)
          }
        }
        // Checklist runs (type 14) and Checklist steps (type 15)
        const checklistIdFilters = { checklist_name: 'checklist_ids', event_host: 'event_host_ids', started_by: 'started_by_ids', completed_by: 'completed_by_ids', reviewed_by: 'reviewed_by_ids', done_by: 'done_by_ids' }
        for(const ck in checklistIdFilters){
          if(this.filters[ck].actual !== '' && this.filters[ck].actual.length > 0){
            const ids = this.filters[ck].value.map(o => o.id)
            url += '&' + checklistIdFilters[ck] + '=[' + encodeURIComponent(ids) + ']'
          }
        }
        const checklistTextFilters = ['run_status', 'requires_approval', 'checklist_archived', 'review_note', 'run_tag', 'step_name', 'step_required',
          'response_type', 'step_done', 'step_response', 'step_note', 'issue_flagged', 'issue_description', 'step_tag']
        for(const ck of checklistTextFilters){
          if(this.filters[ck].actual !== ''){
            url += '&' + ck + '=' + encodeURIComponent(this.filters[ck].value)
          }
        }
        const checklistDateFilters = ['started_at', 'completed_at', 'reviewed_at', 'done_at']
        for(const ck of checklistDateFilters){
          if(this.filters[ck].actual_to !== '' && this.filters[ck].actual_from !== ''){
            url += '&' + ck + '_from=' + encodeURIComponent(this.filters[ck].value_from) + '&' + ck + '_to=' + encodeURIComponent(this.filters[ck].value_to)
          }
        }
        const checklistNumberFilters = ['duration_minutes', 'steps_total', 'steps_done', 'percent_complete', 'issues_flagged',
          'steps_with_notes', 'steps_with_photos', 'tickets_created', 'step_number', 'minutes_from_start', 'photo_count', 'run_id']
        for(const ck of checklistNumberFilters){
          if(this.filters[ck].actual_from !== '') url += '&' + ck + '_from=' + encodeURIComponent(this.filters[ck].value_from)
          if(this.filters[ck].actual_to !== '') url += '&' + ck + '_to=' + encodeURIComponent(this.filters[ck].value_to)
        }

        if(this.filters.custom_field.actual != ''){
          let custom_field_arr = []
          for(let n in this.filters.custom_field.value){
            custom_field_arr.push(this.filters.custom_field.value[n].id)
          }
          url += '&custom_field=[' + encodeURIComponent(custom_field_arr) + ']'
        }
        if(this.filters.payment_methods.actual != ''){
          url += '&payment_methods=' + encodeURIComponent(this.filters.payment_methods.value)
        }
        if(this.filters.payment_collected_by.actual !== '' && this.filters.payment_collected_by.actual.length > 0){
          let payment_collected_by_arr = []
          for(let n in this.filters.payment_collected_by.value){
            payment_collected_by_arr.push(this.filters.payment_collected_by.value[n].id)
          }
          url += '&payment_collected_by_users=[' + encodeURIComponent(payment_collected_by_arr) + ']'
        }
        if(this.filters.promo_codes.actual != ''){
          url += '&promo_codes=' + encodeURIComponent(this.filters.promo_codes.value)
        }
        if(this.filters.promo_codes.with_promo_code_actual !== null && this.filters.promo_codes.with_promo_code_actual !== ''){
          url += '&with_promo_code=' + encodeURIComponent(this.filters.promo_codes.with_promo_code_value)
        }
        if(this.filters.card_type.actual != ''){
          url += '&card_type=' + encodeURIComponent(this.filters.card_type.value)
        }
        if(this.filters.expiry.actual != ''){
          url += '&expiry=' + encodeURIComponent(this.filters.expiry.value)
        }
        if(this.filters.last_four.actual != ''){
          url += '&last_four=' + encodeURIComponent(this.filters.last_four.value)
        }
        if(this.filters.payment_gateway_id.actual != ''){
          url += '&payment_gateway_id=' + encodeURIComponent(this.filters.payment_gateway_id.value)
        }
        if(this.filters.first_name.actual != ''){
          url += '&first_name=' + encodeURIComponent(this.filters.first_name.value)
        }
        if(this.filters.last_name.actual != ''){
          url += '&last_name=' + encodeURIComponent(this.filters.last_name.value)
        }
        if(this.filters.source.actual != ''){
          url += '&source=' + encodeURIComponent(this.decapitalizeFirstLetterAddUnderscore(this.filters.source.value))
        }
        for(const uk of ['medium', 'campaign', 'referrer', 'traffic_attribution']){
          if(this.filters[uk].actual != ''){
            url += '&' + uk + '=' + encodeURIComponent(this.filters[uk].value)
          }
        }
        if(this.filters.opted_out_of_receiving_emails.actual != ''){
          url += '&email_optout=' + encodeURIComponent(this.filters.opted_out_of_receiving_emails.value)
        }
        if(this.filters.birthday.actual_to != null && this.filters.birthday.actual_from != null &&
          this.filters.birthday.actual_to != '' && this.filters.birthday.actual_from != ''){
          url += '&birthday_from=' + encodeURIComponent(this.filters.birthday.value_from) +
            '&birthday_to=' + encodeURIComponent(this.filters.birthday.value_to)
        }
        if(this.filters.date_last_visited.actual_to != null && this.filters.date_last_visited.actual_from != null &&
          this.filters.date_last_visited.actual_to != '' && this.filters.date_last_visited.actual_from != ''){
          url += '&date_last_visited_from=' + encodeURIComponent(this.filters.date_last_visited.value_from) +
            '&date_last_visited_to=' + encodeURIComponent(this.filters.date_last_visited.value_to)
        }
        if(this.filters.date_joined.actual_to != null && this.filters.date_joined.actual_from != null &&
          this.filters.date_joined.actual_to != '' && this.filters.date_joined.actual_from != ''){
          url += '&date_joined_from=' + encodeURIComponent(this.filters.date_joined.value_from) +
            '&date_joined_to=' + encodeURIComponent(this.filters.date_joined.value_to)
        }
        if(this.filters.state.actual != ''){
          url += '&state=' + encodeURIComponent(this.filters.state.value)
        }
        if(this.filters.email.actual != ''){
          url += '&email=' + encodeURIComponent(this.filters.email.value)
        }
        if(this.filters.phone.actual != ''){
          url += '&phone=' + encodeURIComponent(this.filters.phone.value)
        }
        if(this.filters.address.actual != ''){
          url += '&address=' + encodeURIComponent(this.filters.address.value)
        }
        if(this.filters.city.actual != ''){
          url += '&city=' + encodeURIComponent(this.filters.city.value)
        }
        if(this.filters.zip.actual != ''){
          url += '&zip=' + encodeURIComponent(this.filters.zip.value)
        }
        if(this.filters.country.actual != ''){
          url += '&country=' + encodeURIComponent(this.filters.country.value)
        }
        if(this.filters.opted_out_of_posting_photos.actual != ''){
          url += '&picture_optout=' + encodeURIComponent(this.filters.opted_out_of_posting_photos.value)
        }
        if(this.filters.transaction_has_due_amount.actual != ''){
          url += '&transaction_has_due_amount=' + encodeURIComponent(this.filters.transaction_has_due_amount.value)
        }
        if(this.filters.player_experience_level.actual != ''){
          url += '&player_experience_level=' + encodeURIComponent(this.filters.player_experience_level.value)
        }
        if(this.filters.referral_source.actual != ''){
          url += '&referral_source=' + encodeURIComponent(this.filters.referral_source.value)
        }
        if(this.filters.tag.actual != ''){
          url += '&tag=' + encodeURIComponent(this.filters.tag.value)
        }
        //Games
        if(this.filters.start_time.actual_to != null && this.filters.start_time.actual_from != null &&
          this.filters.start_time.actual_to != '' && this.filters.start_time.actual_from != '' ){
          let from = this.filters.start_time.value_from
          let to = this.filters.start_time.value_to
          url += '&start_time_from=' + encodeURIComponent(from) +
            '&start_time_to=' + encodeURIComponent(to)
        }
        if(this.filters.end_time.actual_to != null && this.filters.end_time.actual_from != null &&
          this.filters.end_time.actual_to != '' && this.filters.end_time.actual_from != ''){
          let from = this.filters.end_time.value_from
          let to = this.filters.end_time.value_to
          url += '&end_time_from=' + encodeURIComponent(from) +
            '&end_time_to=' + encodeURIComponent(to)
        }
        if(this.filters.game_name.actual !== '' && this.filters.game_name.actual.length > 0){
          let arr = []
          for(let n in this.filters.game_name.value){
            arr.push(this.filters.game_name.value[n].id)
          }
          url += '&games=[' + encodeURIComponent(arr) + ']'
        }
        if(this.filters.team_experience_level.actual != ''){
          url += '&team_experience_level=' + encodeURIComponent(this.filters.team_experience_level.value)
        }
        if(this.filters.pre_game_notes.actual != ''){
          url += '&pre_game_notes=' + encodeURIComponent(this.filters.pre_game_notes.value)
        }
        if(this.filters.post_game_notes.actual != ''){
          url += '&post_game_notes=' + encodeURIComponent(this.filters.post_game_notes.value)
        }
        if(this.filters.completed.actual != ''){
          url += '&completed=' + encodeURIComponent(this.filters.completed.value)
        }
        if(this.filters.game_master.actual !== '' && this.filters.game_master.actual.length > 0){
          let gm_arr = []
          for(let n in this.filters.game_master.value){
            gm_arr.push(this.filters.game_master.value[n].id)
          }
          url += '&game_master=[' + encodeURIComponent(gm_arr) + ']'
        }
        if(this.filters.booking_date.actual_to != null && this.filters.booking_date.actual_from != null &&
          this.filters.booking_date.actual_to != '' && this.filters.booking_date.actual_from != ''){
          url += '&booking_date_from=' + encodeURIComponent(this.filters.booking_date.value_from) +
            '&booking_date_to=' + encodeURIComponent(this.filters.booking_date.value_to)
        }
        if(this.filters.booking_time.actual_to != null && this.filters.booking_time.actual_from != null &&
          this.filters.booking_time.actual_to != '' && this.filters.booking_time.actual_from != ''){
          let from = this.filters.booking_time.value_from
          let to = this.filters.booking_time.value_to
          url += '&booking_time_from=' + encodeURIComponent(from) +
            '&booking_time_to=' + encodeURIComponent(to)
        }
        if(this.filters.number_of_hints.actual_to != '' && this.filters.number_of_hints.actual_from != ''){
          url += '&number_of_hints_to=' + encodeURIComponent(this.filters.number_of_hints.value_to) + '&number_of_hints_from=' + encodeURIComponent(this.filters.number_of_hints.value_from)
        }
        if(this.filters.number_of_breaks.actual_to != '' && this.filters.number_of_breaks.actual_from != ''){
          url += '&number_of_breaks_to=' + encodeURIComponent(this.filters.number_of_breaks.value_to) + '&number_of_breaks_from=' + encodeURIComponent(this.filters.number_of_breaks.value_from)
        }

        //Transactions
        if(this.filters.transaction_id.actual != ''){
          url += '&transaction_id=' + encodeURIComponent(this.filters.transaction_id.value)
        }
        if(this.filters.customer_first_name.actual != ''){
          url += '&customer_first_name=' + encodeURIComponent(this.filters.customer_first_name.value)
        }
        if(this.filters.customer_last_name.actual != ''){
          url += '&customer_last_name=' + encodeURIComponent(this.filters.customer_last_name.value)
        }
        if(this.filters.customer_email.actual != ''){
          url += '&customer_email=' + encodeURIComponent(this.filters.customer_email.value)
        }
        if(this.filters.customer_phone.actual != ''){
          url += '&customer_phone=' + encodeURIComponent(this.filters.customer_phone.value)
        }
        if(this.filters.customer_address.actual != ''){
          url += '&customer_address=' + encodeURIComponent(this.filters.customer_address.value)
        }
        if(this.filters.customer_city.actual != ''){
          url += '&customer_city=' + encodeURIComponent(this.filters.customer_city.value)
        }
        if(this.filters.customer_state.actual != ''){
          url += '&customer_state=' + encodeURIComponent(this.filters.customer_state.value)
        }
        if(this.filters.customer_zip.actual != ''){
          url += '&customer_zip=' + encodeURIComponent(this.filters.customer_zip.value)
        }
        if(this.filters.price.actual_to != '' && this.filters.price.actual_from != ''){
          url += '&price_to=' + encodeURIComponent(this.filters.price.value_to) + '&price_from=' + encodeURIComponent(this.filters.price.value_from)
        }
        if(this.filters.customer_credit.actual != ''){
          url += '&customer_credit=' + encodeURIComponent(this.filters.customer_credit.value)
        }
        if(this.filters.fee.actual_to != '' && this.filters.fee.actual_from != ''){
          url += '&fee_to=' + encodeURIComponent(this.filters.fee.value_to) + '&fee_from=' + encodeURIComponent(this.filters.fee.value_from)
        }
        if(this.filters.tips.actual_to != '' && this.filters.tips.actual_from != ''){
          url += '&tips_to=' + encodeURIComponent(this.filters.tips.value_to) + '&tips_from=' + encodeURIComponent(this.filters.tips.value_from)
        }
        if(this.filters.tip_assignees.actual != ''){
          url += '&tip_assignees=' + encodeURIComponent(this.filters.tip_assignees.value)
        }
        if(this.filters.transaction_notes.actual != ''){
          url += '&transaction_notes=' + encodeURIComponent(this.filters.transaction_notes.value)
        }
        if(this.filters.tax_names.actual != ''){
          url += '&tax_names=' + encodeURIComponent(this.filters.tax_names.value)
        }
        if(this.fieldExists('purchases_in_transaction') && this.filters.purchase_name.actual !== '' && this.filters.purchase_name.actual.length > 0){
          let purchases_arr = []
          for(let n in this.filters.purchase_name.value){
            purchases_arr.push(JSON.stringify({id: this.filters.purchase_name.value[n].id, name: this.filters.purchase_name.value[n].name}))
          }
          url += '&purchases_in_transaction=[' + encodeURIComponent(purchases_arr) + ']'
        }
        if(this.filters.discount.actual_to != '' && this.filters.discount.actual_from != ''){
          url += '&discount_to=' + encodeURIComponent(this.filters.discount.value_to) + '&discount_from=' + encodeURIComponent(this.filters.discount.value_from)
        }
        if(this.filters.tax.actual_to != '' && this.filters.tax.actual_from != ''){
          url += '&tax_to=' + encodeURIComponent(this.filters.tax.value_to) + '&tax_from=' + encodeURIComponent(this.filters.tax.value_from)
        }
        if(this.filters.total.actual_to != '' && this.filters.total.actual_from != ''){
          url += '&total_to=' + encodeURIComponent(this.filters.total.value_to) + '&total_from=' + encodeURIComponent(this.filters.total.value_from)
        }
        if(this.filters.paid.actual_to != '' && this.filters.paid.actual_from != ''){
          url += '&paid_to=' + encodeURIComponent(this.filters.paid.value_to) + '&paid_from=' + encodeURIComponent(this.filters.paid.value_from)
        }
        if(this.filters.refunded.actual_to != '' && this.filters.refunded.actual_from != ''){
          url += '&refunded_to=' + encodeURIComponent(this.filters.refunded.value_to) + '&refunded_from=' + encodeURIComponent(this.filters.refunded.value_from)
        }
        if(this.filters.due.actual_to != '' && this.filters.due.actual_from != ''){
          url += '&due_to=' + encodeURIComponent(this.filters.due.value_to) + '&due_from=' + encodeURIComponent(this.filters.due.value_from)
        }
        if(this.filters.status.actual != ''){
          url += '&status=' + encodeURIComponent(this.filters.status.value)
        }
        if(this.filters.transaction_date.actual_to != null && this.filters.transaction_date.actual_from != null &&
          this.filters.transaction_date.actual_to != '' && this.filters.transaction_date.actual_from != ''){
          let userStartDate = this.filters.transaction_date.value_from
          let userEndDate = this.filters.transaction_date.value_to


          let startLocalDate = new Date(`${userStartDate}T00:00:00`);
          let endLocalDate = new Date(`${userEndDate}T23:59:59`);

          let from = startLocalDate.toISOString();
          let to = endLocalDate.toISOString();

          url += '&transaction_date_from=' + from +
            '&transaction_date_to=' + to
        }
        //Purchases
        if(this.filters.purchase_time.actual_to != null && this.filters.purchase_time.actual_from != null &&
          this.filters.purchase_time.actual_to != '' && this.filters.purchase_time.actual_from != ''){
          let userStartDate = this.filters.purchase_time.value_from
          let userEndDate = this.filters.purchase_time.value_to


          let startLocalDate = new Date(`${userStartDate}T00:00:00`);
          let endLocalDate = new Date(`${userEndDate}T23:59:59`);

          let from = startLocalDate.toISOString();
          let to = endLocalDate.toISOString();
          url += '&purchase_time_from=' + from +
            '&purchase_time_to=' + to
        }
        //Tip date
        if(this.filters.tip_date.actual_to != null && this.filters.tip_date.actual_from != null &&
          this.filters.tip_date.actual_to != '' && this.filters.tip_date.actual_from != ''){
          let userStartDate = this.filters.tip_date.value_from
          let userEndDate = this.filters.tip_date.value_to


          let startLocalDate = new Date(`${userStartDate}T00:00:00`);
          let endLocalDate = new Date(`${userEndDate}T23:59:59`);

          let from = startLocalDate.toISOString();
          let to = endLocalDate.toISOString();
          url += '&tip_date_from=' + from +
            '&tip_date_to=' + to
        }
        if(this.filters.response_time.actual_to != null && this.filters.response_time.actual_from != null &&
          this.filters.response_time.actual_to != '' && this.filters.response_time.actual_from != ''){
          let userStartDate = this.filters.response_time.value_from
          let userEndDate = this.filters.response_time.value_to


          let startLocalDate = new Date(`${userStartDate}T00:00:00`);
          let endLocalDate = new Date(`${userEndDate}T23:59:59`);

          let from = startLocalDate.toISOString();
          let to = endLocalDate.toISOString();
          url += '&response_time_from=' + from +
            '&response_time_to=' + to
        }
        if(this.filters.purchase_name.actual !== '' && this.filters.purchase_name.actual.length > 0){
          let purchase_name_arr = []
          for(let n in this.filters.purchase_name.value){
            purchase_name_arr.push(JSON.stringify({id: this.filters.purchase_name.value[n].id, name: this.filters.purchase_name.value[n].name}))
          }
          url += '&purchases=[' + encodeURIComponent(purchase_name_arr) + ']'
        }
        if(this.filters.purchase_type.actual !== '' && this.filters.purchase_type.actual.length > 0){
          let purchase_type_arr = []
          for(let n in this.filters.purchase_type.value){
            purchase_type_arr.push(this.filters.purchase_type.value[n].name)
          }
          url += '&purchase_types=[' + encodeURIComponent(purchase_type_arr) + ']'
        }
        if((this.filters.quantity.actual_to != '' && this.filters.quantity.actual_from != '') || this.filters.quantity.unlimited){
          let q1 = this.filters.quantity.unlimited ? -1 : this.filters.quantity.value_to
          let q2 = this.filters.quantity.unlimited ? -1 : this.filters.quantity.value_from
          url += '&quantity_to=' + encodeURIComponent(q1) + '&quantity_from=' + encodeURIComponent(q2)
        }
        //promo codes
        if(this.filters.promo_code_name.actual !== '' && this.filters.promo_code_name.actual.length > 0){
          let promo_code_name_arr = []
          for(let n in this.filters.promo_code_name.value){
            promo_code_name_arr.push(this.filters.promo_code_name.value[n].id)
          }
          url += '&promo_code_names=[' + encodeURIComponent(promo_code_name_arr) + ']'
        }
        if(this.filters.description.actual !== ''){
          url += '&description=' + encodeURIComponent(this.filters.description.value)
        }
        if(this.filters.redemption_type.actual !== ''){
          url += '&redemption_type=' + encodeURIComponent(this.filters.redemption_type.value)
        }
        if(this.filters.value_type.actual !== ''){
          url += '&value_type=' + encodeURIComponent(this.filters.value_type.value)
        }
        if(this.filters.value_amount.actual_to != '' && this.filters.value_amount.actual_from != ''){
          url += '&value_amount_to=' + encodeURIComponent(this.filters.value_amount.value_to) + '&value_amount_from=' + encodeURIComponent(this.filters.value_amount.value_from)
        }
        if(this.filters.can_be_combined.actual !== ''){
          url += '&can_be_combined=' + encodeURIComponent(this.filters.can_be_combined.value)
        }
        if(this.filters.issued_by_campaign.actual !== ''){
          url += '&issued_by_campaign=' + encodeURIComponent(this.filters.issued_by_campaign.value)
        }
        if(this.filters.number_of_times_used.actual_to != '' && this.filters.number_of_times_used.actual_from != ''){
          url += '&number_of_times_used_to=' + encodeURIComponent(this.filters.number_of_times_used.value_to) + '&number_of_times_used_from=' + encodeURIComponent(this.filters.number_of_times_used.value_from)
        }

        if(this.filters.applicable_to_miscellaneous_items.actual !== ''){
          url += '&applicable_to_miscellaneous_items=' + encodeURIComponent(this.filters.applicable_to_miscellaneous_items.value)
        }
        if(this.filters.internal_only.actual !== ''){
          url += '&internal_only=' + encodeURIComponent(this.filters.internal_only.value)
        }

        if(this.filters.custom_field_type.actual !== ''){
          url += '&custom_field_type=' + encodeURIComponent(this.filters.custom_field_type.value)
        }
        if(this.filters.response.actual != ''){
          url += '&response=' + encodeURIComponent(this.filters.response.value)
        }

        if(this.filters.games.actual !== '' && this.filters.games.actual.length > 0){
          let arr = []
          for(let n in this.filters.games.value){
            arr.push(this.filters.games.value[n].name)
          }
          url += '&games=' + encodeURIComponent(JSON.stringify(arr))
        }

        //Project management filters (mirror buildUrl so CSV export respects them)
        if(this.filters.projects.actual !== '' && this.filters.projects.actual.length > 0){
          let arr = []
          for(let n in this.filters.projects.value){ arr.push(this.filters.projects.value[n].id) }
          url += '&projects=[' + encodeURIComponent(arr) + ']'
        }
        if(this.filters.statuses.actual !== '' && this.filters.statuses.actual.length > 0){
          let arr = []
          for(let n in this.filters.statuses.value){ arr.push(this.filters.statuses.value[n].id) }
          url += '&statuses=[' + encodeURIComponent(arr) + ']'
        }
        if(this.filters.priorities.actual !== '' && this.filters.priorities.actual.length > 0){
          let arr = []
          for(let n in this.filters.priorities.value){ arr.push(`"${this.filters.priorities.value[n]}"`) }
          url += '&priorities=[' + encodeURIComponent(arr) + ']'
        }
        if(this.filters.assignees.actual !== '' && this.filters.assignees.actual.length > 0){
          let arr = []
          for(let n in this.filters.assignees.value){ arr.push(this.filters.assignees.value[n].id) }
          url += '&assignees=[' + encodeURIComponent(arr) + ']'
        }
        if(this.filters.reporters.actual !== '' && this.filters.reporters.actual.length > 0){
          let arr = []
          for(let n in this.filters.reporters.value){ arr.push(this.filters.reporters.value[n].id) }
          url += '&reporters=[' + encodeURIComponent(arr) + ']'
        }
        if(this.filters.title.actual !== ''){
          url += '&title=' + encodeURIComponent(this.filters.title.value)
        }
        if(this.filters.ticket_key.actual !== ''){
          url += '&ticket_key=' + encodeURIComponent(this.filters.ticket_key.value)
        }
        if(this.filters.due_date.actual_to !== null && this.filters.due_date.actual_from !== null &&
          this.filters.due_date.actual_to !== '' && this.filters.due_date.actual_from !== ''){
          url += '&due_date_from=' + encodeURIComponent(this.filters.due_date.value_from) +
            '&due_date_to=' + encodeURIComponent(this.filters.due_date.value_to)
        }
        if(this.filters.resolved_date.actual_to !== null && this.filters.resolved_date.actual_from !== null &&
          this.filters.resolved_date.actual_to !== '' && this.filters.resolved_date.actual_from !== ''){
          url += '&resolved_date_from=' + encodeURIComponent(this.filters.resolved_date.value_from) +
            '&resolved_date_to=' + encodeURIComponent(this.filters.resolved_date.value_to)
        }
        if(this.filters.users.actual !== '' && this.filters.users.actual.length > 0){
          let arr = []
          for(let n in this.filters.users.value){ arr.push(this.filters.users.value[n].id) }
          url += '&users=[' + encodeURIComponent(arr) + ']'
        }
        if(this.filters.categories.actual !== '' && this.filters.categories.actual.length > 0){
          let arr = []
          for(let n in this.filters.categories.value){ arr.push(this.filters.categories.value[n].id) }
          url += '&categories=[' + encodeURIComponent(arr) + ']'
        }
        if(this.filters.is_running.actual !== ''){
          url += '&is_running=' + encodeURIComponent(this.filters.is_running.value)
        }
        if(this.filters.location_checks.actual !== '' && this.filters.location_checks.actual.length > 0){
          url += '&location_checks=' + encodeURIComponent(JSON.stringify(this.filters.location_checks.value))
        }
        if(this.filters.meters_from_venue.actual_from !== ''){
          url += '&meters_from_venue_from=' + encodeURIComponent(this.filters.meters_from_venue.value_from)
        }
        if(this.filters.meters_from_venue.actual_to !== ''){
          url += '&meters_from_venue_to=' + encodeURIComponent(this.filters.meters_from_venue.value_to)
        }

        if(this.summaryActive){
          url += '&summary=1'
          if(this.summaryGroupBy){ url += '&group_by=' + encodeURIComponent(this.summaryGroupBy) }
          if(this.summaryBucket){ url += '&bucket=' + encodeURIComponent(this.summaryBucket) }
        } else {
          url += '&summary=0'
        }
        if(this.sortBy){
          url += '&order_by=' + encodeURIComponent(this.sortBy) + '&order_dir=' + (this.sortDesc ? 'desc' : 'asc')
        }

        let retries = 0;
        let succeeded = false;
        let lastError = null
        while (retries < maxRetries) {
          try {
            const response = await axios.get(url, config);
            csvData.push(...response.data.result); // Flatten and add directly
            this.rowsFetched = Math.min(offset + response.data.result.length, this.totalRows)
            if (this.downloadPhase === 'preparing') {
              this.downloadPhase = 'downloading'
            }
            this.retryingAttempt = 0;
            succeeded = true;
            break; // Exit retry loop on success
          } catch (err) {
            const status = err.response ? err.response.status : null
            const body = err.response && typeof err.response.data === 'string' ? err.response.data : ''
            // The lambda gives up on a page that exceeds its query ceiling. Halve the page
            // and re-request the same offset; this does not count as a failed attempt.
            if (status === 400 && body.includes('too large') && count > minCount) {
              count = Math.max(minCount, Math.floor(count / 2))
              url = url.replace(/([?&])count=\d+/, `$1count=${count}`)
              continue
            }
            retries++;
            this.retryingAttempt = retries;
            lastError = err

            if (retries >= maxRetries) {
              console.error(`Request for offset ${offset} failed after ${maxRetries} attempts.`, err);
              break
            }
            await new Promise(resolve => setTimeout(resolve, retryDelaysMs[retries - 1]))
            // A long download can outlive the session token it started with; re-read it so
            // the retry carries a fresh one instead of repeating the same rejected header.
            config.headers.Authorization = await this.getAuthToken()
          }
        }
        if (!succeeded) {
          const error = new Error(`Download failed at offset ${offset} after ${maxRetries} attempts.`)
          error.partialData = csvData
          error.failedAtOffset = offset
          error.status = lastError && lastError.response ? lastError.response.status : null
          error.serverMessage = lastError && lastError.response && typeof lastError.response.data === 'string' ? lastError.response.data : ''
          throw error
        }
        offset = offset + count
        this.percent_downloaded = ((offset/this.totalRows) * 100).toFixed(0)
      }

      let arr = []
      csvData.forEach((element) => {
        arr = arr.concat(element)

      })
      return arr
    },
    resetDownloadState(){
      this.downloading = false
      this.percent_downloaded = 0
      this.rowsFetched = 0
      this.retryingAttempt = 0
      this.downloadPhase = null
      this.cancelling = false
      this.downloadStartedAt = null
      this.downloadCancelled = false
    },
    triggerCsvDownload(csvData){
      let fields = Object.keys(this.data[0]);
      let csv = this.capitalizeFirstLetter(fields[0].replaceAll('_', ' '))
      for(let n = 1; n < fields.length; n++){
        csv += ', ' + this.capitalizeFirstLetter(fields[n].replaceAll('_', ' '))
      }
      csv += "\n";


      csvData.forEach(row => {
        let f = []
        for (const [key, value] of Object.entries(row)) {
          let val = value
          //Transaction status is 0/1; ticket status (report type 9) is already a name string.
          if(key === 'status' && this.report.report_type_id !== 9){
            val = value == 0 ? 'Cancelled' : 'Active'
          }
          if(key === 'source'){
            val = (value) ? (this.escapeCSVValue(this.capitalizeFirstLetterRemoveUnderscore(value))) : ''
          }
          // Not escaped here: every value is escaped once at the end of this loop.
          if(key === 'medium' || key === 'campaign'){
            val = (value) ? formatUtmValue(value) : ''
          }
          if(key === 'referrer' || key === 'traffic_attribution'){
            val = (value) ? value : ''
          }
          if(key === 'birthday' || key === 'booking_date' || key === 'due_date'){
            val = (value && !String(value).includes('0000')) ? this.getFormattedDate(value) : ''
          }
          if(key === 'availability_start_date' || key === 'availability_end_date'){
            val = (value && !String(value).includes('0000')) ? this.getFormattedDate(value) : ''
          }
          if(key === 'availability_start_time' || key === 'availability_end_time'){
            val = (value) ? this.getTime(value) : ''
          }
          if(key === 'event_date'){
            val = (value && !String(value).includes('0000')) ? this.getFormattedDate(value) : ''
          }
          if(key === 'event_time'){
            val = (value) ? this.getTime(value) : ''
          }
          if(key === 'signed_date'){
            val = (value) ? this.getFormattedDate(value) + ' ' + this.getTime(value) : ''
          }
          if(key === 'started_at' || key === 'completed_at' || key === 'reviewed_at' || key === 'done_at'){
            val = (value) ? this.getFormattedDate(value) + ' ' + this.getTime(value) : ''
          }
          if(key === 'tips_collected'){
            val = this.getAmount(value)
          }
          if(key === 'staff_name' || key === 'staff_email' || key === 'staff_nickname' || key === 'staff_role'
            || key === 'event_name' || key === 'involvement'){
            val = (value) ? this.escapeCSVValue(value) : ''
          }
          if(key === 'order_number'){
            val = (value) ? '#' + value : ''
          }
          if(key === 'duration' || key === 'time_logged' || key === 'time_estimate'){
            val = this.formatMinutes(value)
          }
          if(key === 'is_running'){
            val = (value) ? 'Yes' : 'No'
          }
          if(key === 'priority' && this.report.report_type_id === 9){
            val = this.priorityLabel(value)
          }
          if(key === 'first_name' || key === 'customer_first_name' || key === 'customer_last_name'
            || key === 'last_name' || key === 'referral_source' || key === 'payment_gateway_transaction_id'
            || key === 'player_experience_level' || key === 'card_type' || key === 'games'){
            val = (value) ? this.capitalizeFirstLetter(this.escapeCSVValue(value)) : ''
          }
          if(key === 'booking_time' || key === 'start_time' || key === 'end_time'){
            //Time tracking (report type 10) start/end are full datetimes; games are time-only.
            if(this.report.report_type_id === 10 && (key === 'start_time' || key === 'end_time')){
              val = (value && !String(value).includes('0000'))
                ? this.escapeCSVValue(this.getFormattedDate(value)) + ' ' + this.escapeCSVValue(this.getTime(value))
                : ''
            } else {
              val = this.getTime(value)
            }
          }
          if(key === 'completed' || key === 'opted_out_of_posting_photos' || key === 'transaction_has_due_amount'
            || key === 'opted_out_of_receiving_emails' || key === 'can_be_combined' || key === 'internal_only' || key === 'issued_by_campaign'
            || key === 'applicable_to_miscellaneous_items'){
            val = (value) ? 'Yes' : 'No'
          }
          if(key === 'redemption_type'){
            val = (value) ? 'Percentage discount' : 'Value discount'
          }
          if(key === 'value_type'){
            val = (value === 0) ? 'Per transaction' : 'Per participant'
          }
          if(key === 'transaction_date' || key === 'purchase_time' || key === 'response_time' || key === 'date_joined' || key === 'date_last_visited'
            || key === 'created_date' || key === 'resolved_date' || key === 'last_updated' || key === 'created_at'){
            if(!value || value.includes('0000')){
              val = ''
            } else {
              val = this.escapeCSVValue(this.getFormattedDate(value)) + ' ' + this.escapeCSVValue(this.getTime(value))
            }
          }
          if(key === 'expiry'){
            val = (value) ? this.getExpiry(value) : ''
          }
          if(key === 'last_four' || key === 'payment_collected_by' || key === 'description'){
            val = (value) ? (value) : ''
          }
          if(key === 'promo_codes' || key === 'tip_assignees'){
            val = (value) ? this.escapeCSVValue(value) : ''
          }
          if(key === 'tax_names' || key === 'tags'){
            val = (value) ? value : ''
          }
          if(key === 'tip_date'){
            val = (value) ? this.formatTipDates(value) : ''
          }
          if(key === 'payment_method'){
            val = (value) ? this.getPaymentMethod(value) : ''
          }
          if(key === 'transaction_id'){
            val = (value) ? '#' + value : ''
          }
          if(key === 'quantity'){
            val = (value === -1) ? 'Unlimited' : (value)
          }
          if(key === 'due' || key === 'tips' || key === 'tax' || key === 'fee' || key === 'total' || key === 'refunded' || key === 'customer_credit'
            || key === 'price' || key === 'paid' || key === 'discount' || key === 'value_amount'){
            val = this.getAmount(value)
          }

          // CSV is a data export: missing values are blank cells, never a '-' placeholder
          // (a '-' is text and breaks sorting, numeric columns, and re-imports).
          if(val === null || val === undefined){
            val = ''
          }
          // These columns come back from the report query with '-' as their own
          // empty sentinel; blank them by key so a genuine '-' elsewhere is preserved.
          if((key === 'tags' || key === 'transaction_notes') && value === '-'){
            val = ''
          }
          val = this.escapeCSVValue(val)

          f.push(val);
        }

        csv += f
        csv += "\n";
      });

      const anchor = document.createElement('a');
      anchor.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv);
      anchor.target = '_blank';
      anchor.download = 'report.csv';
      anchor.click();
    },
    triggerSummaryCsvDownload(rows){
      const fields = this.tableFields
      let csv = fields.map(f => this.escapeCSVValue(f.label)).join(', ') + "\n"
      rows.forEach(row => {
        const line = fields.map(f => {
          const raw = row[f.key]
          const val = f.formatter ? f.formatter(raw) : raw
          // Blank, not '-', for missing values: the on-screen placeholder is display-only,
          // so read the raw value and only use the formatter when there is something to format.
          return this.escapeCSVValue(raw === null || raw === undefined || raw === '' ? '' : String(val))
        })
        csv += line.join(',') + "\n"
      })
      const anchor = document.createElement('a');
      anchor.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv);
      anchor.target = '_blank';
      anchor.download = 'report.csv';
      anchor.click();
    },
    async downloadCSV(){
      this.downloadCancelled = false;
      this.rowsFetched = 0
      this.retryingAttempt = 0
      this.cancelling = false
      this.downloadPhase = 'preparing'
      this.downloadStartedAt = Date.now()
      let token = await this.getAuthToken()
      let config = { headers: { Authorization: token } }

      let count = 5000
      // Purchases pages rebuild the whole UNION per request, so fewer, larger pages are
      // cheaper; the download loop halves the page size if the lambda reports a page too large.
      if(this.report.report_type_id === 4){ count = 1000 }
      this.downloading = true
      let csvData
      try {
        csvData = await this.getAllData(config, count)
      } catch (err) {
        this.downloading = false
        console.error(err)
        const partialRows = (err.partialData && err.partialData.length) ? err.partialData.flat().length : 0
        let reason = ''
        if (err.status === 401 || err.status === 403) {
          reason = ' Your session expired during the download.'
        } else if (err.serverMessage) {
          reason = ' ' + err.serverMessage
        } else if (err.status) {
          reason = ` The server responded with status ${err.status}.`
        }
        const result = await swal({
          title: 'Download failed',
          text: partialRows > 0
            ? `We couldn't finish your report.${reason} You can retry, or download the ${partialRows.toLocaleString()} rows collected so far.`
            : `We couldn't finish your report.${reason} Please try again.`,
          type: 'error',
          showCancelButton: true,
          confirmButtonText: 'Retry',
          cancelButtonText: partialRows > 0 ? `Download ${partialRows.toLocaleString()} rows` : 'Close',
        })
        if (result.value === true) {
          this.resetDownloadState()
          return this.downloadCSV()
        }
        if (partialRows > 0 && result.dismiss === 'cancel' && err.partialData) {
          this.triggerCsvDownload(err.partialData.flat())
          this.$notify({ type: 'warning', message: `Partial report downloaded (${partialRows.toLocaleString()} rows)`, timeout: 4000 })
        }
        this.resetDownloadState()
        return
      }

      if(csvData === null || this.downloadCancelled) {
        this.resetDownloadState()
        swal('Download cancelled', 'The report download was cancelled.', 'info');
        return;
      }

      if(this.summaryActive){
        this.triggerSummaryCsvDownload(csvData)
      } else {
        this.triggerCsvDownload(csvData)
      }
      const finalCount = csvData.length.toLocaleString()
      this.$notify({ type: 'success', message: `Report downloaded (${finalCount} rows)`, timeout: 4000 })
      this.resetDownloadState()
    },
    escapeCSVValue(value) {
      if (typeof value === 'string') {
        // Open a leading = + - @ as text, never as a spreadsheet formula.
        value = neutralizeCsvFormula(value);
        // Escape double quotes by doubling them
        value = value.replace(/"/g, '""');

        // If the value contains a comma, wrap it in double quotes
        if (value.includes(',') || value.includes('\n')) {
          value = `"${value}"`;
        }
      }
      return value;
    },
    // Force Excel to treat date values as text to preserve formatting
    getLocalTime(d){
      let local = new Date(d)
      let mins =  local.getMinutes()
      if(mins < 10){
        mins = '0' + mins
      }
      return this.getTime(local.getHours() + ':' + mins)
    },
    formatTipDates(tipDateString) {
      if (!tipDateString) return '-';

      // Split comma-separated timestamps and format each as date only
      return tipDateString.split(', ').map(dateStr => {
        return this.getFormattedDate(dateStr);
      }).join(', ');
    },
    getAmount(integer) {
      if (integer == null) {
        return;
      }

      // Get currency info
      const currencyInfo = _.find(this.currencies, { code: this.currency });
      const symbol = currencyInfo ? currencyInfo.symbol : this.currency_symbol;
      const isPostfix = currencyInfo ? currencyInfo.postfix : false;

      let formattedAmount;

      if (this.number_format === 1) {
        // European formatting (comma as decimal separator)
        formattedAmount = integer.toLocaleString(undefined, { minimumFractionDigits: 2 }).replace('.', ',');
      } else {
        // Standard formatting (dot as decimal separator)
        formattedAmount = parseFloat(integer).toFixed(2);
      }

      if (this.currency_format === 0) {
        // Symbol format
        if (isPostfix) {
          return formattedAmount + symbol;  // 12.34kr
        } else {
          return symbol + formattedAmount;  // $12.34
        }
      } else {
        // Currency code format
        return formattedAmount + ' ' + this.currency;  // 12.34 SEK
      }
    },
    getTime(d) {
      if(d instanceof Date){
        d = d.toISOString()
      }
      return time(this.time_format, d)
    },
    getFormattedDate(d){
      if(d === '0000-00-00'){
        return '-'
      }
      return formattedDate(this.date_format, d)
    },
    formatMinutes(mins){
      if(mins == null || mins === '') return '-'
      let m = parseInt(mins)
      if(isNaN(m)) return '-'
      if(m === 0) return '0m'
      let h = Math.floor(m / 60)
      let rem = m % 60
      let parts = []
      if(h > 0) parts.push(h + 'h')
      if(rem > 0) parts.push(rem + 'm')
      return parts.join(' ')
    },
    getAuthToken() {
      return new Promise ( function(resolve) {
        Auth.currentSession()
          .then(data =>{
            resolve(data.idToken.jwtToken)
          })
          .catch(err => console.log(err))
      })
    },
    buildUrl(){
      let count = this.perPage
      let offset = parseInt((this.currentPage * this.perPage) - this.perPage)
      let companyId = localStorage.getItem('companyId')
      let companyGroupId = JSON.parse(localStorage.getItem('group')).id
      let reportId = this.$route.query.id
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/reports/' + reportId +
        '/run?offset=' + offset + `&count=` + count

      if(this.filters.group_name.actual != ''){
        let group_arr = []
        for(let n in this.filters.group_name.value){
          group_arr.push(this.filters.group_name.value[n].id)
        }
        url += '&groups=[' + encodeURIComponent(group_arr) + ']'
      }

      if(this.filters.resource_name.actual != ''){
        url += '&resource_name=' + encodeURIComponent(this.filters.resource_name.value)
      }
      if(this.filters.resource_type.actual != ''){
        url += '&resource_type=' + encodeURIComponent(this.filters.resource_type.value)
      }
      if(this.filters.resource_category.actual != '' && this.filters.resource_category.value.length > 0){
        let resource_category_arr = []
        for(let n in this.filters.resource_category.value){
          resource_category_arr.push(this.filters.resource_category.value[n].id)
        }
        url += '&resource_category=[' + encodeURIComponent(resource_category_arr) + ']'
      }
      if(this.filters.capacity.actual_from !== '') url += '&capacity_from=' + encodeURIComponent(this.filters.capacity.value_from)
      if(this.filters.capacity.actual_to !== '') url += '&capacity_to=' + encodeURIComponent(this.filters.capacity.value_to)
      if(this.filters.available_hours.actual_from !== '') url += '&available_hours_from=' + encodeURIComponent(this.filters.available_hours.value_from)
      if(this.filters.available_hours.actual_to !== '') url += '&available_hours_to=' + encodeURIComponent(this.filters.available_hours.value_to)
      if(this.filters.booked_hours.actual_from !== '') url += '&booked_hours_from=' + encodeURIComponent(this.filters.booked_hours.value_from)
      if(this.filters.booked_hours.actual_to !== '') url += '&booked_hours_to=' + encodeURIComponent(this.filters.booked_hours.value_to)
      if(this.filters.idle_hours.actual_from !== '') url += '&idle_hours_from=' + encodeURIComponent(this.filters.idle_hours.value_from)
      if(this.filters.idle_hours.actual_to !== '') url += '&idle_hours_to=' + encodeURIComponent(this.filters.idle_hours.value_to)
      if(this.filters.utilization.actual_from !== '') url += '&utilization_from=' + encodeURIComponent(this.filters.utilization.value_from)
      if(this.filters.utilization.actual_to !== '') url += '&utilization_to=' + encodeURIComponent(this.filters.utilization.value_to)
      if(this.filters.bookings_assigned.actual_from !== '') url += '&bookings_assigned_from=' + encodeURIComponent(this.filters.bookings_assigned.value_from)
      if(this.filters.bookings_assigned.actual_to !== '') url += '&bookings_assigned_to=' + encodeURIComponent(this.filters.bookings_assigned.value_to)
      if(this.filters.availability_start_date.actual_to !== '' && this.filters.availability_start_date.actual_from !== ''){
        url += '&availability_start_date_from=' + encodeURIComponent(this.filters.availability_start_date.value_from) + '&availability_start_date_to=' + encodeURIComponent(this.filters.availability_start_date.value_to)
      }
      if(this.filters.availability_end_date.actual_to !== '' && this.filters.availability_end_date.actual_from !== ''){
        url += '&availability_end_date_from=' + encodeURIComponent(this.filters.availability_end_date.value_from) + '&availability_end_date_to=' + encodeURIComponent(this.filters.availability_end_date.value_to)
      }
      if(this.filters.availability_start_time.actual_to !== '' && this.filters.availability_start_time.actual_from !== ''){
        url += '&availability_start_time_from=' + encodeURIComponent(this.filters.availability_start_time.value_from) + '&availability_start_time_to=' + encodeURIComponent(this.filters.availability_start_time.value_to)
      }
      if(this.filters.availability_end_time.actual_to !== '' && this.filters.availability_end_time.actual_from !== ''){
        url += '&availability_end_time_from=' + encodeURIComponent(this.filters.availability_end_time.value_from) + '&availability_end_time_to=' + encodeURIComponent(this.filters.availability_end_time.value_to)
      }

      // Staff activity (type 12)
      if(this.filters.staff_members.actual !== '' && this.filters.staff_members.actual.length > 0){
        let staff_arr = []
        for(let n in this.filters.staff_members.value){
          staff_arr.push(this.filters.staff_members.value[n].id)
        }
        url += '&staff_ids=[' + encodeURIComponent(staff_arr) + ']'
      }
      if(this.filters.staff_role.actual !== ''){
        url += '&staff_role=' + encodeURIComponent(this.filters.staff_role.value)
      }
      if(this.filters.staff_email.actual !== ''){
        url += '&staff_email=' + encodeURIComponent(this.filters.staff_email.value)
      }
      if(this.filters.event_name.actual !== '' && this.filters.event_name.actual.length > 0){
        let event_arr = []
        for(let n in this.filters.event_name.value){
          event_arr.push(this.filters.event_name.value[n].id)
        }
        url += '&event_names=[' + encodeURIComponent(event_arr) + ']'
      }
      if(this.filters.event_date.actual_to !== '' && this.filters.event_date.actual_from !== ''){
        url += '&event_date_from=' + encodeURIComponent(this.filters.event_date.value_from) + '&event_date_to=' + encodeURIComponent(this.filters.event_date.value_to)
      }
      if(this.filters.event_time.actual_to !== '' && this.filters.event_time.actual_from !== ''){
        url += '&event_time_from=' + encodeURIComponent(this.filters.event_time.value_from) + '&event_time_to=' + encodeURIComponent(this.filters.event_time.value_to)
      }
      if(this.filters.involvement.actual !== ''){
        url += '&involvement=' + encodeURIComponent(this.filters.involvement.value)
      }
      if(this.filters.order_number.actual !== ''){
        url += '&order_number=' + encodeURIComponent(this.filters.order_number.value)
      }
      if(this.filters.tips_collected.actual_from !== '') url += '&tips_collected_from=' + encodeURIComponent(this.filters.tips_collected.value_from)
      if(this.filters.tips_collected.actual_to !== '') url += '&tips_collected_to=' + encodeURIComponent(this.filters.tips_collected.value_to)

      // Waivers (type 13). Shared text and date filters ride the customer report's blocks above.
      const waiverSelectFilters = ['is_minor', 'waiver_type', 'signature_on_file', 'photo_opt_out',
        'email_opt_out', 'not_participating', 'created_internally']
      for(const wk of waiverSelectFilters){
        if(this.filters[wk].actual !== ''){
          url += '&' + wk + '=' + encodeURIComponent(this.filters[wk].value)
        }
      }
      if(this.filters.signed_date.actual_to !== '' && this.filters.signed_date.actual_from !== ''){
        url += '&signed_date_from=' + encodeURIComponent(this.filters.signed_date.value_from) + '&signed_date_to=' + encodeURIComponent(this.filters.signed_date.value_to)
      }
      for(const gk of ['guardian_email', 'guardian_phone']){
        if(this.filters[gk].actual !== ''){
          url += '&' + gk + '=' + encodeURIComponent(this.filters[gk].value)
        }
      }
      // Checklist runs (type 14) and Checklist steps (type 15)
      const checklistIdFilters = { checklist_name: 'checklist_ids', event_host: 'event_host_ids', started_by: 'started_by_ids', completed_by: 'completed_by_ids', reviewed_by: 'reviewed_by_ids', done_by: 'done_by_ids' }
      for(const ck in checklistIdFilters){
        if(this.filters[ck].actual !== '' && this.filters[ck].actual.length > 0){
          const ids = this.filters[ck].value.map(o => o.id)
          url += '&' + checklistIdFilters[ck] + '=[' + encodeURIComponent(ids) + ']'
        }
      }
      const checklistTextFilters = ['run_status', 'requires_approval', 'checklist_archived', 'review_note', 'run_tag', 'step_name', 'step_required',
        'response_type', 'step_done', 'step_response', 'step_note', 'issue_flagged', 'issue_description', 'step_tag']
      for(const ck of checklistTextFilters){
        if(this.filters[ck].actual !== ''){
          url += '&' + ck + '=' + encodeURIComponent(this.filters[ck].value)
        }
      }
      const checklistDateFilters = ['started_at', 'completed_at', 'reviewed_at', 'done_at']
      for(const ck of checklistDateFilters){
        if(this.filters[ck].actual_to !== '' && this.filters[ck].actual_from !== ''){
          url += '&' + ck + '_from=' + encodeURIComponent(this.filters[ck].value_from) + '&' + ck + '_to=' + encodeURIComponent(this.filters[ck].value_to)
        }
      }
      const checklistNumberFilters = ['duration_minutes', 'steps_total', 'steps_done', 'percent_complete', 'issues_flagged',
        'steps_with_notes', 'steps_with_photos', 'tickets_created', 'step_number', 'minutes_from_start', 'photo_count', 'run_id']
      for(const ck of checklistNumberFilters){
        if(this.filters[ck].actual_from !== '') url += '&' + ck + '_from=' + encodeURIComponent(this.filters[ck].value_from)
        if(this.filters[ck].actual_to !== '') url += '&' + ck + '_to=' + encodeURIComponent(this.filters[ck].value_to)
      }

      if(this.filters.custom_field.actual != ''){
        let custom_field_arr = []
        for(let n in this.filters.custom_field.value){
          custom_field_arr.push(this.filters.custom_field.value[n].id)
        }
        url += '&custom_field=[' + encodeURIComponent(custom_field_arr) + ']'
      }
      if(this.filters.payment_methods.actual !== ''){
        url += '&payment_methods=' + encodeURIComponent(this.filters.payment_methods.value)
      }
      if(this.filters.promo_codes.actual !== ''){
        url += '&promo_codes=' + encodeURIComponent(this.filters.promo_codes.value)
      }
      if(this.filters.promo_codes.with_promo_code_actual !== null && this.filters.promo_codes.with_promo_code_actual !== ''){
        url += '&with_promo_code=' + encodeURIComponent(this.filters.promo_codes.with_promo_code_value)
      }
      if(this.filters.payment_collected_by.actual !== '' && this.filters.payment_collected_by.actual.length > 0){
        let payment_collected_by_arr = []
        for(let n in this.filters.payment_collected_by.value){
          payment_collected_by_arr.push(this.filters.payment_collected_by.value[n].id)
        }
        url += '&payment_collected_by_users=[' + encodeURIComponent(payment_collected_by_arr) + ']'
      }
      if(this.filters.card_type.actual !== ''){
        url += '&card_type=' + encodeURIComponent(this.filters.card_type.value)
      }
      if(this.filters.expiry.actual !== ''){
        url += '&expiry=' + encodeURIComponent(this.filters.expiry.value)
      }
      if(this.filters.last_four.actual !== ''){
        url += '&last_four=' + encodeURIComponent(this.filters.last_four.value)
      }
      if(this.filters.payment_gateway_id.actual !== ''){
        url += '&payment_gateway_id=' + encodeURIComponent(this.filters.payment_gateway_id.value)
      }
      if(this.filters.first_name.actual !== ''){
        url += '&first_name=' + encodeURIComponent(this.filters.first_name.value)
      }
      if(this.filters.last_name.actual !== ''){
        url += '&last_name=' + encodeURIComponent(this.filters.last_name.value)
      }
      if(this.filters.source.actual !== ''){
        url += '&source=' + encodeURIComponent(this.decapitalizeFirstLetterAddUnderscore(this.filters.source.value))
      }
      for(const uk of ['medium', 'campaign', 'referrer', 'traffic_attribution']){
        if(this.filters[uk].actual !== ''){
          url += '&' + uk + '=' + encodeURIComponent(this.filters[uk].value)
        }
      }
      if(this.filters.opted_out_of_receiving_emails.actual !== ''){
        url += '&email_optout=' + encodeURIComponent(this.filters.opted_out_of_receiving_emails.value)
      }
      if(this.filters.transaction_has_due_amount.actual !== ''){
        url += '&transaction_has_due_amount=' + encodeURIComponent(this.filters.transaction_has_due_amount.value)
      }
      if(this.filters.birthday.actual_to !== null && this.filters.birthday.actual_from != null &&
        this.filters.birthday.actual_to !== '' && this.filters.birthday.actual_from != ''){
        url += '&birthday_from=' + encodeURIComponent(this.filters.birthday.value_from) +
          '&birthday_to=' + encodeURIComponent(this.filters.birthday.value_to)
      }
      if(this.filters.date_last_visited.actual_to !== null && this.filters.date_last_visited.actual_from != null &&
        this.filters.date_last_visited.actual_to !== '' && this.filters.date_last_visited.actual_from != ''){
        url += '&date_last_visited_from=' + encodeURIComponent(this.filters.date_last_visited.value_from) +
          '&date_last_visited_to=' + encodeURIComponent(this.filters.date_last_visited.value_to)
      }
      if(this.filters.date_joined.actual_to !== null && this.filters.date_joined.actual_from != null &&
        this.filters.date_joined.actual_to !== '' && this.filters.date_joined.actual_from != ''){
        url += '&date_joined_from=' + encodeURIComponent(this.filters.date_joined.value_from) +
          '&date_joined_to=' + encodeURIComponent(this.filters.date_joined.value_to)
      }
      if(this.filters.state.actual !== ''){
        url += '&state=' + encodeURIComponent(this.filters.state.value)
      }
      if(this.filters.email.actual !== ''){
        url += '&email=' + encodeURIComponent(this.filters.email.value)
      }
      if(this.filters.phone.actual !== ''){
        url += '&phone=' + encodeURIComponent(this.filters.phone.value)
      }
      if(this.filters.address.actual !== ''){
        url += '&address=' + encodeURIComponent(this.filters.address.value)
      }
      if(this.filters.city.actual !== ''){
        url += '&city=' + encodeURIComponent(this.filters.city.value)
      }
      if(this.filters.zip.actual !== ''){
        url += '&zip=' + encodeURIComponent(this.filters.zip.value)
      }
      if(this.filters.country.actual !== ''){
        url += '&country=' + encodeURIComponent(this.filters.country.value)
      }
      if(this.filters.opted_out_of_posting_photos.actual !== ''){
        url += '&picture_optout=' + encodeURIComponent(this.filters.opted_out_of_posting_photos.value)
      }
      if(this.filters.player_experience_level.actual !== ''){
        url += '&player_experience_level=' + encodeURIComponent(this.filters.player_experience_level.value)
      }
      if(this.filters.referral_source.actual !== ''){
        url += '&referral_source=' + encodeURIComponent(this.filters.referral_source.value)
      }
      if(this.filters.tag.actual !== ''){
        url += '&tag=' + encodeURIComponent(this.filters.tag.value)
      }

      //Games
      if(this.filters.start_time.actual_to !== null && this.filters.start_time.actual_from !== null &&
        this.filters.start_time.actual_to !== '' && this.filters.start_time.actual_from !== '' ){
        let from = this.filters.start_time.value_from
        let to = this.filters.start_time.value_to
        url += '&start_time_from=' + encodeURIComponent(from) +
          '&start_time_to=' + encodeURIComponent(to)
      }
      if(this.filters.end_time.actual_to !== null && this.filters.end_time.actual_from !== null &&
        this.filters.end_time.actual_to !== '' && this.filters.end_time.actual_from !== ''){
        let from = this.filters.end_time.value_from
        let to = this.filters.end_time.value_to
        url += '&end_time_from=' + encodeURIComponent(from) +
          '&end_time_to=' + encodeURIComponent(to)
      }
      if(this.filters.game_name.actual !== '' && this.filters.game_name.actual.length > 0){
        let arr = []
        for(let n in this.filters.game_name.value){
          arr.push(this.filters.game_name.value[n].id)
        }
        url += '&games=[' + encodeURIComponent(arr) + ']'
      }
      if(this.filters.team_experience_level.actual !== ''){
        url += '&team_experience_level=' + encodeURIComponent(this.filters.team_experience_level.value)
      }
      if(this.filters.pre_game_notes.actual !== ''){
        url += '&pre_game_notes=' + encodeURIComponent(this.filters.pre_game_notes.value)
      }
      if(this.filters.post_game_notes.actual !== ''){
        url += '&post_game_notes=' + encodeURIComponent(this.filters.post_game_notes.value)
      }
      if(this.filters.completed.actual !== ''){
        url += '&completed=' + encodeURIComponent(this.filters.completed.value)
      }
      if(this.filters.game_master.actual !== '' && this.filters.game_master.actual.length > 0){
        let gm_arr = []
        for(let n in this.filters.game_master.value){
          gm_arr.push(this.filters.game_master.value[n].id)
        }
        url += '&game_master=[' + encodeURIComponent(gm_arr) + ']'
      }
      if(this.filters.booking_date.actual_to !== null && this.filters.booking_date.actual_from !== null &&
        this.filters.booking_date.actual_to !== '' && this.filters.booking_date.actual_from !== ''){
        url += '&booking_date_from=' + encodeURIComponent(this.filters.booking_date.value_from) +
          '&booking_date_to=' + encodeURIComponent(this.filters.booking_date.value_to)
      }
      if(this.filters.booking_time.actual_to !== null && this.filters.booking_time.actual_from !== null &&
        this.filters.booking_time.actual_to !== '' && this.filters.booking_time.actual_from !== ''){
        let from = this.filters.booking_time.value_from
        let to = this.filters.booking_time.value_to
        url += '&booking_time_from=' + encodeURIComponent(from) +
          '&booking_time_to=' + encodeURIComponent(to)
      }
      if(this.filters.number_of_hints.actual_to !== '' && this.filters.number_of_hints.actual_from !== ''){
        url += '&number_of_hints_to=' + encodeURIComponent(this.filters.number_of_hints.value_to) +  '&number_of_hints_from=' + encodeURIComponent(this.filters.number_of_hints.value_from)
      }
      if(this.filters.number_of_breaks.actual_to !== '' && this.filters.number_of_breaks.actual_from !== ''){
        url += '&number_of_breaks_to=' + encodeURIComponent(this.filters.number_of_breaks.value_to) +  '&number_of_breaks_from=' + encodeURIComponent(this.filters.number_of_breaks.value_from)
      }

      //Transactions
      if(this.filters.transaction_id.actual !== ''){
        url += '&transaction_id=' + encodeURIComponent(this.filters.transaction_id.value)
      }
      if(this.filters.customer_first_name.actual !== ''){
        url += '&customer_first_name=' + encodeURIComponent(this.filters.customer_first_name.value)
      }
      if(this.filters.customer_last_name.actual !== ''){
        url += '&customer_last_name=' + encodeURIComponent(this.filters.customer_last_name.value)
      }
      if(this.filters.customer_email.actual !== ''){
        url += '&customer_email=' + encodeURIComponent(this.filters.customer_email.value)
      }
      if(this.filters.customer_phone.actual !== ''){
        url += '&customer_phone=' + encodeURIComponent(this.filters.customer_phone.value)
      }
      if(this.filters.customer_address.actual !== ''){
        url += '&customer_address=' + encodeURIComponent(this.filters.customer_address.value)
      }
      if(this.filters.customer_city.actual !== ''){
        url += '&customer_city=' + encodeURIComponent(this.filters.customer_city.value)
      }
      if(this.filters.customer_state.actual !== ''){
        url += '&customer_state=' + encodeURIComponent(this.filters.customer_state.value)
      }
      if(this.filters.customer_zip.actual !== ''){
        url += '&customer_zip=' + encodeURIComponent(this.filters.customer_zip.value)
      }
      if(this.filters.price.actual_to !== '' && this.filters.price.actual_from !== ''){
        url += '&price_to=' + encodeURIComponent(this.filters.price.value_to) +  '&price_from=' + encodeURIComponent(this.filters.price.value_from)
      }
      if(this.filters.customer_credit.actual !== ''){
        url += '&customer_credit=' + encodeURIComponent(this.filters.customer_credit.value)
      }
      if(this.filters.fee.actual_to !== '' && this.filters.fee.actual_from !== ''){
        url += '&fee_to=' + encodeURIComponent(this.filters.fee.value_to) +  '&fee_from=' + encodeURIComponent(this.filters.fee.value_from)
      }
      if(this.filters.tips.actual_to !== '' && this.filters.tips.actual_from !== ''){
        url += '&tips_to=' + encodeURIComponent(this.filters.tips.value_to) +  '&tips_from=' + encodeURIComponent(this.filters.tips.value_from)
      }
      if(this.filters.tip_assignees.actual !== ''){
        url += '&tip_assignees=' + encodeURIComponent(this.filters.tip_assignees.value)
      }
      if(this.filters.transaction_notes.actual !== ''){
        url += '&transaction_notes=' + encodeURIComponent(this.filters.transaction_notes.value)
      }
      if(this.filters.tax_names.actual !== ''){
        url += '&tax_names=' + encodeURIComponent(this.filters.tax_names.value)
      }
      if(this.fieldExists('purchases_in_transaction') && this.filters.purchase_name.actual !== '' && this.filters.purchase_name.actual.length > 0){
        let purchases_arr = []
        for(let n in this.filters.purchase_name.value){
          purchases_arr.push(JSON.stringify({id: this.filters.purchase_name.value[n].id, name: this.filters.purchase_name.value[n].name}))
        }
        url += '&purchases_in_transaction=[' + encodeURIComponent(purchases_arr) + ']'
      }
      if(this.filters.discount.actual_to !== '' && this.filters.discount.actual_from !== ''){
        url += '&discount_to=' + encodeURIComponent(this.filters.discount.value_to) +  '&discount_from=' + encodeURIComponent(this.filters.discount.value_from)
      }
      if(this.filters.tax.actual_to !== '' && this.filters.tax.actual_from !== ''){
        url += '&tax_to=' + encodeURIComponent(this.filters.tax.value_to) +  '&tax_from=' + encodeURIComponent(this.filters.tax.value_from)
      }
      if(this.filters.total.actual_to !== '' && this.filters.total.actual_from !== ''){
        url += '&total_to=' + encodeURIComponent(this.filters.total.value_to) +  '&total_from=' + encodeURIComponent(this.filters.total.value_from)
      }
      if(this.filters.paid.actual_to !== '' && this.filters.paid.actual_from !== ''){
        url += '&paid_to=' + encodeURIComponent(this.filters.paid.value_to) +  '&paid_from=' + encodeURIComponent(this.filters.paid.value_from)
      }
      if(this.filters.refunded.actual_to !== '' && this.filters.refunded.actual_from !== ''){
        url += '&refunded_to=' + encodeURIComponent(this.filters.refunded.value_to) +  '&refunded_from=' + encodeURIComponent(this.filters.refunded.value_from)
      }
      if(this.filters.due.actual_to !== '' && this.filters.due.actual_from !== ''){
        url += '&due_to=' + encodeURIComponent(this.filters.due.value_to) +  '&due_from=' + encodeURIComponent(this.filters.due.value_from)
      }
      if(this.filters.status.actual !== ''){
        url += '&status=' + encodeURIComponent(this.filters.status.value)
      }
      if(this.filters.transaction_date.actual_to !== null && this.filters.transaction_date.actual_from !== null &&
        this.filters.transaction_date.actual_to !== '' && this.filters.transaction_date.actual_from !== ''){
        let userStartDate = this.filters.transaction_date.value_from
        let userEndDate = this.filters.transaction_date.value_to


        let startLocalDate = new Date(`${userStartDate}T00:00:00`);
        let endLocalDate = new Date(`${userEndDate}T23:59:59`);

        let from = startLocalDate.toISOString();
        let to = endLocalDate.toISOString();

        url += '&transaction_date_from=' + from +
          '&transaction_date_to=' + to

      }
      //Purchases
      if(this.filters.purchase_time.actual_to !== null && this.filters.purchase_time.actual_from !== null &&
        this.filters.purchase_time.actual_to !== '' && this.filters.purchase_time.actual_from !== ''){
        let userStartDate = this.filters.purchase_time.value_from
        let userEndDate = this.filters.purchase_time.value_to


        let startLocalDate = new Date(`${userStartDate}T00:00:00`);
        let endLocalDate = new Date(`${userEndDate}T23:59:59`);

        let from = startLocalDate.toISOString();
        let to = endLocalDate.toISOString();
        url += '&purchase_time_from=' + from +
          '&purchase_time_to=' + to
      }
      //Tip date
      if(this.filters.tip_date.actual_to !== null && this.filters.tip_date.actual_from !== null &&
        this.filters.tip_date.actual_to !== '' && this.filters.tip_date.actual_from !== ''){
        let userStartDate = this.filters.tip_date.value_from
        let userEndDate = this.filters.tip_date.value_to


        let startLocalDate = new Date(`${userStartDate}T00:00:00`);
        let endLocalDate = new Date(`${userEndDate}T23:59:59`);

        let from = startLocalDate.toISOString();
        let to = endLocalDate.toISOString();
        url += '&tip_date_from=' + from +
          '&tip_date_to=' + to
      }
      if(this.filters.response_time.actual_to !== null && this.filters.response_time.actual_from !== null &&
        this.filters.response_time.actual_to !== '' && this.filters.response_time.actual_from !== ''){
        let userStartDate = this.filters.response_time.value_from
        let userEndDate = this.filters.response_time.value_to


        let startLocalDate = new Date(`${userStartDate}T00:00:00`);
        let endLocalDate = new Date(`${userEndDate}T23:59:59`);

        let from = startLocalDate.toISOString();
        let to = endLocalDate.toISOString();
        url += '&response_time_from=' + from +
          '&response_time_to=' + to
      }
      if(this.filters.purchase_name.actual !== '' && this.filters.purchase_name.actual.length > 0){
        let purchase_name_arr = []
        for(let n in this.filters.purchase_name.value){
          purchase_name_arr.push(JSON.stringify({id: this.filters.purchase_name.value[n].id, name: this.filters.purchase_name.value[n].name}))
        }
        url += '&purchases=[' + encodeURIComponent(purchase_name_arr) + ']'
      }
      if(this.filters.purchase_type.actual !== '' && this.filters.purchase_type.actual.length > 0){
        let purchase_type_arr = []
        for(let n in this.filters.purchase_type.value){
          purchase_type_arr.push(this.filters.purchase_type.value[n].name)
        }
        url += '&purchase_types=[' + encodeURIComponent(purchase_type_arr) + ']'
      }
      if((this.filters.quantity.actual_to != '' && this.filters.quantity.actual_from != '') || this.filters.quantity.unlimited){
        let q1 = this.filters.quantity.unlimited ? -1 : this.filters.quantity.value_to
        let q2 = this.filters.quantity.unlimited ? -1 : this.filters.quantity.value_from
        url += '&quantity_to=' + encodeURIComponent(q1) + '&quantity_from=' + encodeURIComponent(q2)
      }

      //promo codes
      if(this.filters.promo_code_name.actual !== ''  && this.filters.promo_code_name.actual.length > 0){
        let promo_code_name_arr = []
        for(let n in this.filters.promo_code_name.value){
          promo_code_name_arr.push(this.filters.promo_code_name.value[n].id)
        }
        url += '&promo_code_names=[' + encodeURIComponent(promo_code_name_arr) + ']'
      }
      if(this.filters.description.actual !== ''){
        url += '&description=' + encodeURIComponent(this.filters.description.value)
      }
      if(this.filters.redemption_type.actual !== ''){
        url += '&redemption_type=' + encodeURIComponent(this.filters.redemption_type.value)
      }
      if(this.filters.value_type.actual !== ''){
        url += '&value_type=' + encodeURIComponent(this.filters.value_type.value)
      }
      if(this.filters.value_amount.actual_to != '' && this.filters.value_amount.actual_from != ''){
        url += '&value_amount_to=' + encodeURIComponent(this.filters.value_amount.value_to) + '&value_amount_from=' + encodeURIComponent(this.filters.value_amount.value_from)
      }
      if(this.filters.can_be_combined.actual !== ''){
        url += '&can_be_combined=' + encodeURIComponent(this.filters.can_be_combined.value)
      }
      if(this.filters.issued_by_campaign.actual !== ''){
        url += '&issued_by_campaign=' + encodeURIComponent(this.filters.issued_by_campaign.value)
      }

      if(this.filters.number_of_times_used.actual_to != '' && this.filters.number_of_times_used.actual_from != ''){
        url += '&number_of_times_used_to=' + encodeURIComponent(this.filters.number_of_times_used.value_to) + '&number_of_times_used_from=' + encodeURIComponent(this.filters.number_of_times_used.value_from)
      }
      if(this.filters.applicable_to_miscellaneous_items.actual !== ''){
        url += '&applicable_to_miscellaneous_items=' + encodeURIComponent(this.filters.applicable_to_miscellaneous_items.value)
      }
      if(this.filters.internal_only.actual !== ''){
        url += '&internal_only=' + encodeURIComponent(this.filters.internal_only.value)
      }
      if(this.filters.games.actual !== '' && this.filters.games.actual.length > 0){
        let arr = []
        for(let n in this.filters.games.value){
          arr.push(this.filters.games.value[n].name);
        }
        url += '&games=' + encodeURIComponent(JSON.stringify(arr))
      }

      if(this.filters.custom_field_type.actual !== ''){
        url += '&custom_field_type=' + encodeURIComponent(this.filters.custom_field_type.value)
      }

      if(this.filters.response.actual !== ''){
        url += '&response=' + encodeURIComponent(this.filters.response.value)
      }

      //Project management — tickets
      if(this.filters.projects.actual !== '' && this.filters.projects.actual.length > 0){
        let arr = []
        for(let n in this.filters.projects.value){
          arr.push(this.filters.projects.value[n].id)
        }
        url += '&projects=[' + encodeURIComponent(arr) + ']'
      }
      if(this.filters.statuses.actual !== '' && this.filters.statuses.actual.length > 0){
        let arr = []
        for(let n in this.filters.statuses.value){
          arr.push(this.filters.statuses.value[n].id)
        }
        url += '&statuses=[' + encodeURIComponent(arr) + ']'
      }
      if(this.filters.priorities.actual !== '' && this.filters.priorities.actual.length > 0){
        let arr = []
        for(let n in this.filters.priorities.value){
          arr.push(`"${this.filters.priorities.value[n]}"`)
        }
        url += '&priorities=[' + encodeURIComponent(arr) + ']'
      }
      if(this.filters.assignees.actual !== '' && this.filters.assignees.actual.length > 0){
        let arr = []
        for(let n in this.filters.assignees.value){
          arr.push(this.filters.assignees.value[n].id)
        }
        url += '&assignees=[' + encodeURIComponent(arr) + ']'
      }
      if(this.filters.reporters.actual !== '' && this.filters.reporters.actual.length > 0){
        let arr = []
        for(let n in this.filters.reporters.value){
          arr.push(this.filters.reporters.value[n].id)
        }
        url += '&reporters=[' + encodeURIComponent(arr) + ']'
      }
      if(this.filters.title.actual !== ''){
        url += '&title=' + encodeURIComponent(this.filters.title.value)
      }
      if(this.filters.ticket_key.actual !== ''){
        url += '&ticket_key=' + encodeURIComponent(this.filters.ticket_key.value)
      }
      if(this.filters.due_date.actual_to !== null && this.filters.due_date.actual_from !== null &&
        this.filters.due_date.actual_to !== '' && this.filters.due_date.actual_from !== ''){
        url += '&due_date_from=' + encodeURIComponent(this.filters.due_date.value_from) +
          '&due_date_to=' + encodeURIComponent(this.filters.due_date.value_to)
      }
      if(this.filters.resolved_date.actual_to !== null && this.filters.resolved_date.actual_from !== null &&
        this.filters.resolved_date.actual_to !== '' && this.filters.resolved_date.actual_from !== ''){
        url += '&resolved_date_from=' + encodeURIComponent(this.filters.resolved_date.value_from) +
          '&resolved_date_to=' + encodeURIComponent(this.filters.resolved_date.value_to)
      }

      //Project management — time tracking
      if(this.filters.users.actual !== '' && this.filters.users.actual.length > 0){
        let arr = []
        for(let n in this.filters.users.value){
          arr.push(this.filters.users.value[n].id)
        }
        url += '&users=[' + encodeURIComponent(arr) + ']'
      }
      if(this.filters.categories.actual !== '' && this.filters.categories.actual.length > 0){
        let arr = []
        for(let n in this.filters.categories.value){
          arr.push(this.filters.categories.value[n].id)
        }
        url += '&categories=[' + encodeURIComponent(arr) + ']'
      }
      if(this.filters.is_running.actual !== ''){
        url += '&is_running=' + encodeURIComponent(this.filters.is_running.value)
      }
      if(this.filters.location_checks.actual !== '' && this.filters.location_checks.actual.length > 0){
        url += '&location_checks=' + encodeURIComponent(JSON.stringify(this.filters.location_checks.value))
      }
      if(this.filters.meters_from_venue.actual_from !== ''){
        url += '&meters_from_venue_from=' + encodeURIComponent(this.filters.meters_from_venue.value_from)
      }
      if(this.filters.meters_from_venue.actual_to !== ''){
        url += '&meters_from_venue_to=' + encodeURIComponent(this.filters.meters_from_venue.value_to)
      }

      if(this.summaryActive){
        url += '&summary=1'
        if(this.summaryGroupBy){ url += '&group_by=' + encodeURIComponent(this.summaryGroupBy) }
        if(this.summaryBucket){ url += '&bucket=' + encodeURIComponent(this.summaryBucket) }
      } else {
        url += '&summary=0'
      }
      if(this.sortBy){
        url += '&order_by=' + encodeURIComponent(this.sortBy) + '&order_dir=' + (this.sortDesc ? 'desc' : 'asc')
      }

      return url
    },
    async loadPresetFilters(){
      try {
        let token = await this.getAuthToken()
        let config = {headers: {Authorization: token}}
        let companyId = localStorage.getItem('companyId')
        let companyGroupId = JSON.parse(localStorage.getItem('group')).id
        let reportId = this.$route.query.id
        let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/reports/' + reportId
        const response = await this.axios.get(url, config)
        this.canEditReport = response.data.report.can_write !== 0
        let parsed = {}
        if(response.data.report.filters){
          parsed = JSON.parse(response.data.report.filters)
        }
        if(parsed && typeof parsed === 'object' && Object.keys(parsed).length > 0){
          this.presetFilters = parsed
          // Purchase presets are {id, name} pairs: feed them straight to the internal
          // filter model for the first query, and hold them for chip conversion once
          // the purchases collection arrives with the first response.
          const chipValues = { ...parsed }
          this.pendingPresetPurchases = {}
          for (const f of this.allFilterDefs()) {
            if (f._model === 'multi-purchase' && Array.isArray(parsed[f.key]) && parsed[f.key].length > 0) {
              this.pendingPresetPurchases[f.key] = parsed[f.key]
              delete chipValues[f.key]
            }
          }
          this.applyFilterValues({ ...this.filterValues, ...chipValues })
          for (const key in this.pendingPresetPurchases) {
            if (this.filters[key]) {
              this.filters[key].value = this.pendingPresetPurchases[key]
              this.filters[key].actual = this.pendingPresetPurchases[key]
            }
          }
          this.filterBarKey++
        }
      } catch (err) {
        // Presets are an enhancement; the report must render without them.
        console.log(err)
        this.canEditReport = false
      }
    },
    presetSnapshot(){
      const snap = {}
      for (const f of this.allFilterDefs()) {
        if (f.type === 'daterange') continue
        const v = this.filterValues[f.key]
        if (v == null) continue
        if (f._model === 'multi-purchase') {
          // Chip values are indexes into the purchases union (ids collide across types),
          // so presets store stable {id, name} pairs, the same shape buildUrl() sends.
          const coll = this.purchases || []
          const pairs = (Array.isArray(v) ? v : []).map(idx => { const o = coll[idx] || {}; return { id: o.id, name: o.name } }).filter(o => o.id != null)
          if (pairs.length > 0) snap[f.key] = pairs
          continue
        }
        if (f.type === 'multiselect') {
          if (Array.isArray(v) && v.length > 0) snap[f.key] = v
        } else if (f.type === 'numberrange') {
          if (v.from !== '' || v.to !== '' || v.unlimited) snap[f.key] = v
        } else if (f.type === 'timerange') {
          if (v.from !== '' || v.to !== '') snap[f.key] = v
        } else if (f.type === 'selecttext') {
          if (v.mode !== '' || v.text !== '') snap[f.key] = v
        } else {
          if (v !== '') snap[f.key] = v
        }
      }
      return snap
    },
    async savePresetFilters(){
      const snap = this.presetSnapshot()
      const hasFilters = Object.keys(snap).length > 0
      if(hasFilters && JSON.stringify(snap).length > 8192){
        swal('Error!', 'These filters are too large to save to the template', 'error')
        return
      }
      this.savingPreset = true
      try {
        let token = await this.getAuthToken()
        let config = {headers: {Authorization: token}}
        let companyId = localStorage.getItem('companyId')
        let companyGroupId = JSON.parse(localStorage.getItem('group')).id
        let reportId = this.$route.query.id
        let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/reports/' + reportId
        await this.axios.put(url, { preset_filters_only: true, filters: hasFilters ? snap : null }, config)
        this.presetFilters = hasFilters ? snap : {}
        swal('Success!', 'Preset filters saved to this report', 'success')
      } catch (err) {
        console.log(err)
        if(err.response && err.response.status == 401){
          this.$router.push('/forbidden')
        } else {
          swal('Error!', 'Something went wrong', 'error')
        }
      } finally {
        this.savingPreset = false
      }
    },
    async generateReport(){
      this.loading = true
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let url = this.buildUrl()

      this.axios.get(url, config)
        .then(response => {
          this.loading = false
          this.report = response.data.report
          // Seed summary mode from the saved report exactly once. The first request went
          // out before the saved mode was known, so a saved summary report needs one
          // re-fetch (now with summary=1); the return skips the detail payload it got.
          if(!this.summarySeeded){
            this.summarySeeded = true
            const gf = (response.data.fields || []).find(f => f.report_field_id === this.report.summary_group_by_field_id)
            this.summaryGroupBy = gf ? gf.field : null
            this.summaryBucket = this.report.summary_bucket || 'month'
            if(this.report.is_summary === 1){
              this.summaryActive = true
              this.generateReport()
              return
            }
          }
          this.fields = []
          this.groups = response.data.groups
          this.groupScope = response.data.group_scope || null
          this.games = response.data.games
          this.promo_codes = response.data.promo_codes
          this.company_users = response.data.company_users
          this.checklists = response.data.checklists || []
          this.purchase_types = response.data.purchase_types
          // Add Custom purchase type if not already present
          if(!this.purchase_types.find(pt => pt.name === 'Custom')) {
            this.purchase_types.push({id: 4, name: 'Custom'})
          }
          this.purchases = response.data.purchases
          this.custom_fields = response.data.custom_fields
          this.projects = response.data.projects || []
          this.ticket_statuses = response.data.ticket_statuses || []
          this.time_log_categories = response.data.time_log_categories || []
          this.team_members = response.data.team_members || []

          for(let n in response.data.fields){
            this.fields.push(response.data.fields[n].field)
          }
          this.fieldMeta = response.data.fields || []
          this.data = response.data.result
          // Summary sums arrive as strings (DECIMAL over the legacy mysql driver);
          // b-table sorts raw values, so coerce them for numeric sorting.
          if(response.data.summary === 1){
            this.data = this.data.map(row => {
              const out = { ...row }
              for(const k of Object.keys(out)){
                if(k === 'row_count' || k.startsWith('sum_')){
                  out[k] = out[k] === null ? null : Number(out[k])
                }
              }
              return out
            })
          }
          this.totalRows = response.data.total_rows
          this.resourceEfficiencyTotals = response.data.resource_efficiency_totals || null
          this.resource_categories = response.data.resource_categories || []
          if (Object.keys(this.pendingPresetPurchases).length > 0) {
            const coll = this.purchases || []
            const converted = {}
            for (const key in this.pendingPresetPurchases) {
              converted[key] = this.pendingPresetPurchases[key]
                .map(p => coll.findIndex(x => x.id === p.id && x.name === p.name))
                .filter(i => i >= 0)
            }
            this.filterValues = { ...this.filterValues, ...converted }
            this.pendingPresetPurchases = {}
          }
          this.firstLoadDone = true
        })
        .catch(err => {
          this.loading = false
          console.log(err)
          if(err.response.status == 401){
            this.$router.push('/forbidden')
          } else if(err.response.status == 400 && err.response.data){
            swal('Report too large', err.response.data, 'warning')
          } else {
            swal('Error!', 'Something went wrong', 'error')
          }
        })
    }
  },
  async beforeRouteLeave(to, from, next) {
    if (this.downloading) {
      const result = await swal({
        title: 'Leave this page?',
        text: 'Your report is still downloading. Leaving will cancel the download.',
        type: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Leave',
        cancelButtonText: 'Stay',
      })
      if (!result.value) {
        return next(false)
      }
      this.downloadCancelled = true
    }
    next()
  },
  async mounted() {
    this.currency_symbol =  _.find(this.currencies, { code: JSON.parse(localStorage.getItem('group')).currency }).symbol
    await this.loadPresetFilters()
    this.generateReport()
  }
}
</script>

<style scoped>
.download-progress {
  padding: 0.75rem 1rem;
  border: 1px solid rgba(225, 78, 202, 0.3);
  border-radius: 0.4285rem;
  background: rgba(225, 78, 202, 0.06);
}
.download-progress-label {
  font-size: 0.875rem;
  font-weight: 600;
}
.download-progress-detail {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.7);
}
.download-progress-retry {
  font-size: 0.75rem;
  color: #e14eca;
}
/* Mode row sits tight under the filter bar (which carries 1rem bottom margin of
   its own) and above the table. */
.summary-mode-row {
  gap: 8px;
  margin: -14px 0 8px;
}
.summary-mode-label {
  font-size: 0.8125rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.7);
}
/* bootstrap-vue's own CSS (which draws the sort carets) isn't imported in this
   app, so sortable headers had no visible affordance. Render our own caret +
   pointer on the th[aria-sort] elements b-table sets on sortable columns. */
::v-deep table.b-table thead th[aria-sort] {
  cursor: pointer;
  position: relative;
  padding-right: 1.25rem;
  user-select: none;
}
::v-deep table.b-table thead th[aria-sort]::after {
  content: "";
  position: absolute;
  right: 0.4rem;
  top: 50%;
  transform: translateY(-50%);
  width: 0;
  height: 0;
  border-left: 4px solid transparent;
  border-right: 4px solid transparent;
  border-top: 5px solid rgba(125, 125, 125, 0.55);
}
::v-deep table.b-table thead th[aria-sort="ascending"]::after {
  border-top: none;
  border-bottom: 5px solid #e14eca;
}
::v-deep table.b-table thead th[aria-sort="descending"]::after {
  border-top: 5px solid #e14eca;
  border-bottom: none;
}
</style>

<style>
.white-content .summary-mode-label {
  color: rgba(0, 0, 0, 0.6);
}
/* Resource efficiency report totals row */
::v-deep .resource-efficiency-totals-row td {
  border-top: 2px solid rgba(125, 125, 125, 0.4);
  background: rgba(125, 125, 125, 0.08);
}
</style>
